// ════════════════════════════════════════════════════════════════════════════
// PURPLE CARD EFFECTS
// See js/effects/registry.js for the registerCardEffect() API and the list
// of available triggers.
// ════════════════════════════════════════════════════════════════════════════

// ── Shared helpers (purple-card-local; not part of the registry API) ──

// Directly destroys an enemy unit on the opponent's field, bypassing HP and
// damage mechanics. Nulls the oppField slot, pushes to oppTrash (local mirror),
// sends FIELD_HP_MOD (-99) to tell the peer to remove it from their playerField.
// Vibrates the card first and delays the actual trash-push/network-send by
// the same 500ms every other destroy path uses (see dealDamageToUnit's
// effect-damage-destroy branch in game.js) — previously this fired with no
// animation at all on the ACTING client's own view (the receiving peer's
// FIELD_HP_MOD mirror already vibrates independently in network.js).
function effectDestroyEnemyUnit(target) {
  if (target.tempImmuneToEffectDestruction) {
    addLog(`${target.name} — immune to effect destruction this turn!`);
    return;
  }
  const idx = state.oppField.findIndex(c => c && c.uid === target.uid);
  if (idx === -1) return;
  render(); // with the card still on the field so the element exists for the animation
  const cardEl = vibrateCard('opp', target);
  state.oppField[idx] = null;
  const doDestroy = () => {
    const isToken = isUnitToken(target);
    if (!isToken) {
      state.oppTrashCount++;
      state.oppTrash.push(toTrashCard(target));
    }
    if (target.pilot) {
      state.oppTrashCount++;
      state.oppTrash.push(toTrashCard(target.pilot));
    }
    addLog(`${target.name} is destroyed${isToken ? ' (token — removed from game)' : ''}!`);
    NET.send({ type: 'FIELD_HP_MOD', uids: [target.uid], delta: -99 });
    broadcastCounters();
    render();
    setupDropZones();
  };
  if (cardEl) { setTimeout(doDestroy, 500); } else { doDestroy(); }
}

// Directly destroys ONE OF THE PLAYER'S OWN units on their own field as a
// card-effect cost (e.g. GD05-049/052 Sazabi, GD05-057 Gyunei's Jagd Doga:
// "Choose 1 of your other Units. Destroy it."). Unlike effectDestroyEnemyUnit
// (which mutates a mirrored oppField view and relies on FIELD_HP_MOD to tell
// the real owner to remove it), this runs directly on the true owner's own
// authoritative playerField, so it fires the destroyed unit's own 'destroyed'
// trigger locally — ctx: { cause: 'effect', sourceCard } — letting a card
// like GD05-053 Quess's Jagd Doga check "was I destroyed by one of your
// (Neo Zeon) card's effects" from its own handler — and the generic
// notifyUnitDestroyedByEffect() friendly-watcher hook (e.g. GD05-054 Alpha
// Azieru), then mirrors the removal to the peer's oppField view via the
// existing FIELD_HP_MOD(-99) convention. Also flags
// state.neoZeonEffectDestroyedOwnUnitThisTurn when sourceCard is (Neo Zeon)
// (GD05-129 Axis).
function effectDestroyOwnUnit(target, sourceCard) {
  const idx = state.playerField.findIndex(c => c && c.uid === target.uid);
  if (idx === -1) return;
  state.playerField[idx] = null;
  const isToken = isUnitToken(target);
  if (!isToken) {
    updateTempState(target.cardNo, target.copyVer, 'trash');
    state.trashCount++;
    state.playerTrash.push(toTrashCard(target));
  }
  if (target.pilot) {
    updateTempState(target.pilot.cardNo, target.pilot.copyVer, 'trash');
    state.trashCount++;
    state.playerTrash.push(toTrashCard(target.pilot));
  }
  addLog(`${target.name} is destroyed${isToken ? ' (token — removed from the game)' : ''}!`);
  triggerCardEffect(target, 'destroyed', { cause: 'effect', sourceCard });
  if (target.pilot) {
    triggerCardEffect(target.pilot, 'destroyed', { cause: 'effect', sourceCard, ownerSide: 'player', unit: target });
  }
  notifyUnitDestroyedByEffect(target);
  if (sourceCard && (sourceCard.trait || '').includes('(Neo Zeon)')) {
    state.neoZeonEffectDestroyedOwnUnitThisTurn = true;
  }
  NET.send({ type: 'FIELD_HP_MOD', uids: [target.uid], delta: -99 });
  broadcastCounters();
  render();
  setupDropZones();
}

// Deploys a unit card from the player's HAND directly to the field WITHOUT
// paying its RES cost (e.g. GD05-129 Axis: "deploy 1 (Neo Zeon) Unit card
// that is Lv.3 or lower from your hand" as the payoff of an Activate·Main
// ability, not a normal hand-play). Mirrors the essential parts of the
// generic PLAY_CARD action handler (game.js) — deploy sickness, the two
// force-rested passives, the 'deploy' trigger, PLAY_CARD network mirror —
// just without the spendResources() call, since deploying THIS way is free.
function deployUnitFromHandFree(card, slot) {
  state.hand = state.hand.filter(c => c.uid !== card.uid);
  state.playerField[slot] = card;
  card.tempCantAttack = true; // deploy sickness — cleared by UNREST_ALL on next turn
  if (!isUnitToken(card) && (parseInt(card.level) || 0) <= 3) {
    const forceRested = state.playerField.some(fc =>
      fc && fc.uid !== card.uid && isLinkUnit(fc) &&
      !!(window.CardEffects?.[fc.cardNo]?.passiveForceNonTokenDeployRested?.(fc, card))
    );
    if (forceRested) {
      card.rested = true;
      addLog(`${card.name} — deployed rested (Link Unit passive).`);
    }
  }
  if (!card.rested) {
    const threshold = state.oppField.reduce((max, fc) => {
      if (!fc) return max;
      const t = window.CardEffects?.[fc.cardNo]?.forceEnemyDeployRestedThreshold?.(fc);
      return (typeof t === 'number' && t > max) ? t : max;
    }, -1);
    if (threshold >= 0 && (parseInt(card.level) || 0) <= threshold) {
      card.rested = true;
      addLog(`${card.name} — deployed rested (enemy Unit's passive, Lv.${threshold} or lower).`);
    }
  }
  updateTempState(card.cardNo, card.copyVer, 'field');
  addLog(`${card.name} deployed from hand (no cost) to slot ${(slot % 3) + 1}.`);
  triggerCardEffect(card, 'deploy', { slot });
  NET.send({ type: 'PLAY_CARD', slot, card, counters: myCounters() });
  broadcastCounters();
  render();
  setupDropZones();
}

// Deploy a unit card from the player's trash directly to the field.
// Removes from playerTrash, places in playerField, fires deploy trigger,
// sends PLAY_CARD to peer. `rested` (e.g. ST11-015 "A Twinkle from the
// Abyss": "Deploy it rested.") deploys the unit already rested instead of
// the default active — set BEFORE the PLAY_CARD send below so the peer's
// mirror is correct from the start, not corrected after the fact.
function deployUnitFromTrash(card, slot, { rested = false } = {}) {
  const idx = state.playerTrash.findIndex(c => c.uid === card.uid);
  if (idx !== -1) {
    state.playerTrash.splice(idx, 1);
    state.trashCount = Math.max(0, state.trashCount - 1);
  }
  card.rested = rested;
  card.damaged = false;
  card.tempCantAttack = true;
  state.playerField[slot] = card;
  updateTempState(card.cardNo, card.copyVer, 'field');
  addLog(`${card.name} deployed from trash to field (slot ${(slot % 3) + 1})${rested ? ', rested' : ''}.`);
  triggerCardEffect(card, 'deploy', { slot, fromTrash: true });
  NET.send({ type: 'PLAY_CARD', slot, card, counters: myCounters() });
  broadcastCounters();
  render();
  setupDropZones();
}

// Deploys a Base card from the player's trash directly into the Base zone
// (e.g. GD05-093 Char Aznable: "[When Linked] You may choose 1 (Neo Zeon)
// Base card from your trash. Deploy it."). Mirrors the DEPLOY_BASE action
// handler (game.js) — including the generic applyBaseDeployEffect() shield-
// to-hand mechanic every Base's deployment grants — sourced from trash
// instead of hand. Caller is responsible for checking !state.playerBase
// first (this doesn't handle replacing an existing Base).
function deployBaseFromTrash(baseCard) {
  const idx = state.playerTrash.findIndex(c => c.uid === baseCard.uid);
  if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
  baseCard.rested = false;
  baseCard.damaged = false;
  state.playerBase = baseCard;
  updateTempState(baseCard.cardNo, baseCard.copyVer, 'base');
  applyBaseDeployEffect();
  addLog(`${baseCard.name} deployed from trash as Base.`);
  NET.send({ type: 'DEPLOY_BASE', card: state.playerBase, counters: myCounters() });
  triggerCardEffect(baseCard, 'deploy', {});
  broadcastCounters();
  render();
  setupDropZones();
}

// Returns the effective cost to deploy a card from trash, applying any
// passive trash-cost modifier (e.g. GD03-058 Farsia: trashDeployCostMod = -1).
function getTrashDeployCost(card) {
  const base = parseInt(card.cost) || 0;
  const mod = window.CardEffects?.[card.cardNo]?.trashDeployCostMod?.() || 0;
  return Math.max(0, base + mod);
}

// Exile a card from playerTrash and notify the peer.
function exileFromTrash(card) {
  const idx = state.playerTrash.findIndex(c => c.uid === card.uid);
  if (idx !== -1) state.playerTrash.splice(idx, 1);
  state.trashCount = Math.max(0, state.trashCount - 1);
  updateTempState(card.cardNo, card.copyVer, 'exile');
  state.playerExile.push(card);
  // Full card data (not just uid) — the peer's oppTrash mirror can already be
  // missing this uid (e.g. an earlier trash-add for it never got mirrored),
  // in which case the receiving handler has nothing to move into oppExile
  // and would silently drop the card, undercounting the peer's EXILED total.
  // Sending the card itself lets it fall back to this instead of the mirror.
  NET.send({ type: 'TRASH_EXILE', uid: card.uid, card });
  flashExileCounter('exile-count');
}

// Exiles one or more cards from trash in a single batch, firing ONE
// aggregated toast (singular, or Oxford-comma "A, B, and C" for 3+) instead
// of one per card — matches how effects that exile a whole selection at
// once (e.g. "choose up to 3 cards, exile them") read as a single event.
// Uses currentEffectSourceCard (see its declaration in game.js) for
// attribution, so callers just replace `chosen.forEach(c =>
// exileFromTrash(c))` with `exileCardsFromTrash(chosen)`.
function exileCardsFromTrash(cards) {
  if (!cards || cards.length === 0) return;
  cards.forEach(c => exileFromTrash(c));
  if (currentEffectSourceCard) {
    const names = cards.map(c => c.name);
    const list = names.length === 1 ? names[0]
      : names.length === 2 ? `${names[0]} and ${names[1]}`
      : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
    broadcastCardToast(`${list} was exiled due to ${currentEffectSourceCard.name} effect.`);
  }
}

// ── Card effects ──

// ST05-014 "Fatal Strike"
// [Burst] Choose 1 enemy Unit. Deal 1 damage to it.
// [Main] Choose 1 enemy Unit that is Lv.3 or lower. Destroy it.
registerCardEffect('ST05-014', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 3),
  burst: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
    }, card, { mandatory: true });
  },
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.3 or lower. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.3 or lower. Destroy it.', candidates, (target) => {
      if (target) effectDestroyEnemyUnit(target);
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ST05-013 "With Iron and Blood"
// [Main]/[Action] Choose 1 of your Units. Deal 1 damage to it. It gets AP+3 during this turn.
function st05013Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c);
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly Units in play. No effect.`); onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 of your Units. Deal 1 damage to it. It gets AP+3 during this turn.', candidates, (target) => {
    if (target) {
      dealDamageToUnit(target, 1);
      applyApMod([target], 3, 'tempApMod');
      addLog(`${card.name} — dealt 1 damage to ${target.name}. It gets AP+3 during this turn.`);
      render(); setupDropZones();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('ST05-013', {
  canActivate: () => state.playerField.some(c => c),
  main: (card, ctx) => st05013Effect(card, ctx.onDone),
  action: (card, ctx) => st05013Effect(card, ctx.onDone),
});

// GD02-112 "Momentary Respite"
// [Burst] Draw 1.
// [Main] Choose 1 purple Pilot card from your trash. Add it to your hand.
registerCardEffect('GD02-112', {
  canActivate: () => state.playerTrash.some(c => c.type === 'pilot' && (c.color || '').toLowerCase() === 'purple'),
  burst: (card) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Burst] deck empty, no draw.`);
    }
    broadcastCounters(); render(); setupDropZones();
  },
  main: (card, ctx) => {
    const candidates = state.playerTrash.filter(c => c.type === 'pilot' && (c.color || '').toLowerCase() === 'purple');
    if (candidates.length === 0) {
      addLog(`${card.name} — no purple Pilot cards in trash. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 purple Pilot card from your trash. Add it to your hand.', candidates, (target) => {
      if (target) {
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD02-110 "Awakened Power"
// [Main] Choose 1 Unit card that is Lv.5 or lower from your trash. Pay its cost to deploy it.
// Only units the player can currently afford (cost ≤ available RES) appear in the picker.
// Mandatory once a candidate is chosen — a full field no longer suppresses
// candidates; it instead prompts a trash-for-room choice before deploying.
function gd02110Candidates() {
  const available = state.res + state.exRes;
  return state.playerTrash.filter(c =>
    (c.type === 'unit' || c.type === 'unit-token') &&
    (parseInt(c.level) || 0) <= 5 &&
    getTrashDeployCost(c) <= available
  );
}
registerCardEffect('GD02-110', {
  canActivate: () => gd02110Candidates().length > 0,
  main: (card, ctx) => {
    const candidates = gd02110Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no affordable Unit cards (Lv.5 or lower) in trash. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 Unit card (Lv.5 or lower) from your trash. Pay its cost to deploy it.', candidates, (target) => {
      if (!target) { ctx.onDone(); return; }
      const finishDeploy = (slot) => {
        const cost = getTrashDeployCost(target);
        spendResources(cost);
        broadcastCounters();
        deployUnitFromTrash(target, slot);
        addLog(`${card.name} — deployed ${target.name} from trash (paid cost ${cost}).`);
        ctx.onDone();
      };
      const slot = findNextFieldSlot();
      if (slot !== -1) { finishDeploy(slot); return; }
      promptTrashToMakeRoom(target.name, (freedSlot) => {
        if (freedSlot === -1) { ctx.onDone(); return; }
        finishDeploy(freedSlot);
      }, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// GD02-111 "Decisive Last Resort"
// [Burst] Choose 1 enemy Unit that is Lv.3 or lower. Deal 2 damage to it.
// [Main] Choose 6 purple Unit cards from your trash. Exile them from the game.
//         If you do, choose 1 enemy Unit. Destroy it.
function gd02111PurpleUnits() {
  return state.playerTrash.filter(c => (c.type === 'unit' || c.type === 'unit-token') && (c.color || '').toLowerCase() === 'purple');
}
registerCardEffect('GD02-111', {
  canActivate: () => gd02111PurpleUnits().length >= 6 && state.oppField.some(c => c),
  burst: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.3 or lower). Deal 2 damage to it.', candidates, (target) => {
      if (target) dealDamageToUnit(target, 2, { fromEnemy: true });
    }, card, { mandatory: true });
  },
  main: (card, ctx) => {
    const purpleUnits = gd02111PurpleUnits();
    if (purpleUnits.length < 6) {
      addLog(`${card.name} — fewer than 6 purple Unit cards in trash (${purpleUnits.length}). No effect.`);
      ctx.onDone(); return;
    }
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) {
      addLog(`${card.name} — no enemy Units to destroy. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModalMulti(
      card.name,
      'Choose 6 purple Unit cards from your trash. They will be exiled from the game.',
      purpleUnits,
      { min: 6, max: 6, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 6) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        broadcastCounters(); render();
        showCardPickerModal(card.name, 'Choose 1 enemy Unit. Destroy it.', enemies, (target) => {
          if (target) effectDestroyEnemyUnit(target);
          ctx.onDone();
        }, card, { mandatory: true });
      },
      card
    );
  },
});

// GD03-116 "Towards Destiny"
// [Main]/[Action] Choose 1 friendly (Vagan) Unit and 1 enemy Unit. Deal 2 damage to them.
function gd03116Effect(card, onDone) {
  const friendlies = state.playerField.filter(c => c && (c.trait || '').includes('Vagan'));
  if (friendlies.length === 0) {
    addLog(`${card.name} — no friendly (Vagan) Units in play. No effect.`); onDone(); return;
  }
  const enemies = state.oppField.filter(c => c);
  if (enemies.length === 0) {
    addLog(`${card.name} — no enemy Units in play. No effect.`); onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 friendly (Vagan) Unit. Deal 2 damage to it.', friendlies, (friendly) => {
    if (!friendly) { onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 2 damage to it.', enemies, (enemy) => {
      if (friendly) { dealDamageToUnit(friendly, 2); addLog(`${card.name} — dealt 2 damage to ${friendly.name} (friendly).`); }
      if (enemy) { dealDamageToUnit(enemy, 2, { fromEnemy: true }); addLog(`${card.name} — dealt 2 damage to ${enemy.name} (enemy).`); }
      render(); setupDropZones();
      onDone();
    }, card, { mandatory: true });
  }, card, { mandatory: true });
}
registerCardEffect('GD03-116', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('Vagan')) && state.oppField.some(c => c),
  main: (card, ctx) => gd03116Effect(card, ctx.onDone),
  action: (card, ctx) => gd03116Effect(card, ctx.onDone),
});

// GD03-117 "Orga's Order"
// [Main] If 1–4 enemy Units are in play, deploy 1 [Graze Custom] token.
//         If 5 or more are in play, deploy 1 [Gundam Barbatos 4th Form] token.
registerCardEffect('GD03-117', {
  canActivate: () => state.oppField.some(c => c) && findNextFieldSlot() !== -1,
  main: (card, ctx) => {
    const enemyCount = state.oppField.filter(c => c).length;
    if (enemyCount === 0) {
      addLog(`${card.name} — no enemy Units in play. No effect.`); ctx.onDone(); return;
    }
    if (findNextFieldSlot() === -1) {
      addLog(`${card.name} — no empty field slot. No effect.`); ctx.onDone(); return;
    }
    const tokenNo = enemyCount >= 5 ? 'T-017' : 'T-016';
    const tokenName = enemyCount >= 5 ? 'Gundam Barbatos 4th Form' : 'Graze Custom';
    deployTokenToField(tokenNo);
    addLog(`${card.name} — ${enemyCount} enemy Unit${enemyCount !== 1 ? 's' : ''} in play: deployed [${tokenName}] token.`);
    render(); setupDropZones();
    ctx.onDone();
  },
});

// GD03-114 "Look of Determination"
// [Burst] Activate this card's [Action].
// [Action] Choose 1 active enemy Unit that is Lv.2 or lower. Destroy it.
//          If there are 10 or more cards in your trash, choose Lv.4 or lower instead.
function gd03114GetMaxLv() {
  return state.trashCount >= 10 ? 4 : 2;
}
function gd03114Candidates() {
  const maxLv = gd03114GetMaxLv();
  return state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= maxLv);
}
function gd03114Effect(card, onDone) {
  const candidates = gd03114Candidates();
  if (candidates.length === 0) {
    const maxLv = gd03114GetMaxLv();
    addLog(`${card.name} — no active enemy Units (Lv.${maxLv} or lower) to destroy. No effect.`);
    onDone(); return;
  }
  const maxLv = gd03114GetMaxLv();
  showCardPickerModal(card.name, `Choose 1 active enemy Unit (Lv.${maxLv} or lower). Destroy it.${maxLv === 4 ? ' (10+ cards in trash)' : ''}`, candidates, (target) => {
    if (target) effectDestroyEnemyUnit(target);
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD03-114', {
  canActivate: () => gd03114Candidates().length > 0,
  burst: (card, ctx) => gd03114Effect(card, ctx?.onDone ?? (() => {})),
  action: (card, ctx) => gd03114Effect(card, ctx.onDone),
});

// ST07-013 "Armed Intervention"
// [Burst] Draw 1.
// [Action] Choose 1 rested friendly (CB) Unit. Change the attack target of the
//          battling enemy Unit to it.
// The defender override is applied in runDamageStep (game.js) via
// state.pendingDefenderOverride, and synced to the attacker client via
// CHANGE_ATTACK_TARGET (network.js remoteHandlers).
function st07013CBCandidates() {
  return state.playerField.filter(c => c && c.rested && (c.trait || '').includes('(CB)'));
}
registerCardEffect('ST07-013', {
  canActivate: (card, ctx) => {
    // Only usable during the Action Step when an enemy is attacking
    if (!ctx || ctx.attackerSide !== 'opp') return false;
    return st07013CBCandidates().length > 0;
  },
  burst: (card) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Burst] deck empty, no draw.`);
    }
    broadcastCounters(); render(); setupDropZones();
  },
  action: (card, ctx) => {
    const candidates = st07013CBCandidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested friendly (CB) Units. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested friendly (CB) Unit. The enemy attack is redirected to it.', candidates, (target) => {
      if (target) {
        // Override defender on THIS client
        ctx.battle.defender = target;
        ctx.battle.isShield = false;
        ctx.battle.isDirect = false;
        // Tell the attacker client to redirect too
        NET.send({ type: 'CHANGE_ATTACK_TARGET', uid: target.uid });
        addLog(`${card.name} — attack redirected to ${target.name}.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ST09-009 "Giant Killing"
// [Main]/[Action] Choose 1 active enemy Unit with 4 or less AP. Destroy it.
function st09009Candidates() {
  return state.oppField.filter(c => c && !c.rested && effectiveBp(c) <= 4);
}
function st09009Effect(card, onDone) {
  const candidates = st09009Candidates();
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 4 or less AP. No effect.`); onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 active enemy Unit with 4 or less AP. Destroy it.', candidates, (target) => {
    if (target) effectDestroyEnemyUnit(target);
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('ST09-009', {
  canActivate: () => st09009Candidates().length > 0,
  main: (card, ctx) => st09009Effect(card, ctx.onDone),
  action: (card, ctx) => st09009Effect(card, ctx.onDone),
});

// GD04-114 "Reformationist"
// [Burst] Choose 1 Unit card with "Trans-Am" in its card name from your trash. Add it to your hand.
// [Main]/[Action] Choose 1 of your Units and 1 enemy Unit. Deal 1 damage to them.
function gd04114MainEffect(card, onDone) {
  const friendlies = state.playerField.filter(c => c);
  if (friendlies.length === 0) { addLog(`${card.name} — no friendly Units. No effect.`); onDone(); return; }
  const enemies = state.oppField.filter(c => c);
  if (enemies.length === 0) { addLog(`${card.name} — no enemy Units. No effect.`); onDone(); return; }
  showCardPickerModal(card.name, 'Choose 1 of your Units. Deal 1 damage to it.', friendlies, (friendly) => {
    if (!friendly) { onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 1 damage to it.', enemies, (enemy) => {
      if (friendly) { dealDamageToUnit(friendly, 1); addLog(`${card.name} — dealt 1 damage to ${friendly.name} (friendly).`); }
      if (enemy) { dealDamageToUnit(enemy, 1, { fromEnemy: true }); addLog(`${card.name} — dealt 1 damage to ${enemy.name} (enemy).`); }
      render(); setupDropZones();
      onDone();
    }, card, { mandatory: true });
  }, card, { mandatory: true });
}
registerCardEffect('GD04-114', {
  canActivate: () => {
    // For burst: has Trans-Am unit in trash. For main/action: has units on both sides.
    return state.playerTrash.some(c => (c.type === 'unit' || c.type === 'unit-token') && (c.name || '').includes('Trans-Am'))
      || (state.playerField.some(c => c) && state.oppField.some(c => c));
  },
  burst: (card) => {
    const candidates = state.playerTrash.filter(c =>
      (c.type === 'unit' || c.type === 'unit-token') && (c.name || '').includes('Trans-Am')
    );
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 Unit card with "Trans-Am" in its name from your trash. Add it to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${card.name} — [Burst] recovered ${target.name} from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
      glowHandCard(target.uid);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
  main: (card, ctx) => gd04114MainEffect(card, ctx.onDone),
  action: (card, ctx) => gd04114MainEffect(card, ctx.onDone),
});

// GD04-113 "Damage Control"
// [Burst] Choose 1 enemy Unit. It gets AP-2 during this turn.
// [Action] Choose 1 of your Units. During this battle, reduce battle damage it
//          receives by 3. (Stored as card.battleDefMod; checked in runDamageStep
//          via atkBpNet = max(0, atkBp - battleDefMod). Cleared by CLEAR_TURN_EFFECTS / UNREST_ALL.)
registerCardEffect('GD04-113', {
  canActivate: (card, ctx) => {
    // Burst (outside Action Step) or Action (during battle only)
    if (!ctx) return state.oppField.some(c => c); // burst context
    return state.playerField.some(c => c); // action context
  },
  burst: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — [Burst] ${target.name} gets AP-2 during this turn.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your Units. During this battle, reduce battle damage it receives by 3.', candidates, (target) => {
      if (target) {
        target.battleDefMod = (target.battleDefMod || 0) + 3;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 3, field: 'battleDefMod' });
        addLog(`${card.name} — ${target.name} reduces incoming battle damage by 3 this battle.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD04-115 "Backup"
// [Burst] Choose 1 enemy Unit. Deal 1 damage to it.
// [Main] Choose 1 of your Units. When it deals battle damage to an enemy Unit
//        that is Lv.5 or lower during this turn, destroy that enemy Unit.
//        (card.tempDestroyOnHit = 5; checked in runDamageStep via atkBpForDestCheck.)
registerCardEffect('GD04-115', {
  canActivate: () => state.playerField.some(c => c),
  burst: (card) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 1 damage to it.', candidates, (target) => {
      if (target) dealDamageToUnit(target, 1, { fromEnemy: true });
    }, card, { mandatory: true });
  },
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your Units. When it deals battle damage to an enemy Unit (Lv.5 or lower) this turn, destroy that enemy Unit.', candidates, (target) => {
      if (target) {
        target.tempDestroyOnHit = 5;
        addLog(`${card.name} — ${target.name}: when it deals battle damage to a Lv.5 or lower enemy this turn, that enemy is destroyed.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ── Base cards ──
// ([Burst] Deploy this card and [Deploy] Add 1 of your Shields to your hand
// are handled generically by deployBaseFromShield()/applyBaseDeployEffect()
// in js/game.js. Handlers below only cover effects beyond that boilerplate.)

// ST05-015 "Isaribi"
// [Activate·Main] Rest this Base: Choose 1 of your damaged Units. It gets AP+2 during this turn.
registerCardEffect('ST05-015', {
  canActivate: () => state.playerField.some(c => c && c.damaged),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && c.damaged);
    if (candidates.length === 0) {
      addLog(`${card.name} — no damaged friendly Units. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your damaged Units. It gets AP+2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], 2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// GD02-127 "Freeden"
// [Destroyed] Place the top 2 cards of your deck into your trash.
registerCardEffect('GD02-127', {
  destroyed: (card, ctx) => {
    if (ctx?.defenderSide === 'opp') return; // peer's client during battle — owner handles effect
    const top = peekTopOfDeck(2);
    if (top.length === 0) {
      addLog(`${card.name} — deck is empty, nothing to mill.`);
      ctx.onDone?.();
      return;
    }
    top.forEach(({ entry, card: milled }) => {
      entry.state = 'trash';
      state.trashCount++;
      state.playerTrash.push(toTrashCard(milled));
      NET.send({ type: 'DECK_MILL', card: milled });
      broadcastCardToast(`${milled.name} was sent to trash.`);
      addLog(`${card.name} — [Destroyed] milled ${milled.name} to trash.`);
    });
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    broadcastCounters(); render(); setupDropZones();
    ctx.onDone?.();
  },
});

// GD02-128 "Hammerhead"
// [Deploy] ...Then, if it is your turn and a friendly (Teiwaz) Link Unit is in play,
//          choose 1 enemy Unit with 2 or less AP. Destroy it.
registerCardEffect('GD02-128', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const hasTeiwazLink = state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(Teiwaz)'));
    if (!hasTeiwazLink) return;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 2 or less AP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 2 or less AP. Destroy it.', candidates, (target) => {
      if (target) effectDestroyEnemyUnit(target);
    }, card, { mandatory: true });
  },
});

// GD03-130 "Downes"
// [Deploy] ...Then, if it is your turn, you may choose 1 (Vagan) Unit card (Lv.4 or lower)
//          from your trash. Pay its cost to deploy it.
function gd03130Candidates() {
  const available = state.res + state.exRes;
  const slot = findNextFieldSlot();
  if (slot === -1) return [];
  return state.playerTrash.filter(c =>
    (c.type === 'unit' || c.type === 'unit-token') &&
    (c.trait || '').includes('(Vagan)') &&
    (parseInt(c.level) || 0) <= 4 &&
    getTrashDeployCost(c) <= available
  );
}
registerCardEffect('GD03-130', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const candidates = gd03130Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no affordable (Vagan) Unit cards (Lv.4 or lower) in trash, or field is full. No effect.`);
      return;
    }
    const slot = findNextFieldSlot();
    showCardPickerModal(card.name, 'You may choose 1 (Vagan) Unit card (Lv.4 or lower) from your trash. Pay its cost to deploy it.', candidates, (target) => {
      if (target) {
        const cost = getTrashDeployCost(target);
        spendResources(cost);
        broadcastCounters();
        deployUnitFromTrash(target, slot);
        addLog(`${card.name} — deployed ${target.name} from trash (paid cost ${cost}).`);
      }
    }, card);
  },
});

// ST07-015 "Ptolemaios"
// Passive: While a rested friendly (CB) Unit is in play, this Base can't receive
// damage from enemy Units that are Lv.3 or lower, other than Unit tokens.
// Checked in runDamageStep via the isImmuneToBattleDamageFrom hook.
registerCardEffect('ST07-015', {
  isImmuneToBattleDamageFrom: (base, attacker, defenderSide) => {
    const fieldKey = defenderSide === 'player' ? 'playerField' : 'oppField';
    const hasCBRested = state[fieldKey].some(c => c && c.rested && (c.trait || '').includes('(CB)'));
    if (!hasCBRested) return false;
    return (parseInt(attacker.level) || 0) <= 3 && !isUnitToken(attacker);
  },
});

// GD03-129 "Hotarubi"
// During your turn, when one of your friendly (Tekkadan)/(Teiwaz) Units receives
// effect damage, you may rest this Base to mill the top card of your deck into trash.
// Triggered from dealDamageToUnit via onFriendlyUnitTakesEffectDamage hook.
registerCardEffect('GD03-129', {
  onFriendlyUnitTakesEffectDamage: (base, unit) => {
    const isTekTeiwaz = (unit.trait || '').includes('(Tekkadan)') || (unit.trait || '').includes('(Teiwaz)');
    if (!isTekTeiwaz) return;
    showCardPickerModal(base.name,
      `A friendly (Tekkadan)/(Teiwaz) Unit took effect damage. May rest ${base.name} to mill the top card of your deck into your trash?`,
      [base], (chosen) => {
        if (!chosen) return;
        base.rested = true;
        NET.send({ type: 'UNIT_REST', uid: base.uid, rested: true });
        const top = peekTopOfDeck(1)[0];
        if (top) {
          const { entry, card: milled } = top;
          entry.state = 'trash';
          state.trashCount++;
          state.playerTrash.push(toTrashCard(milled));
          state.deckCount = state.tempDeck.filter(isInDeck).length;
          NET.send({ type: 'DECK_MILL', card: milled });
          broadcastCardToast(`${milled.name} was sent to trash.`);
          addLog(`${base.name} — rested; milled ${milled.name} to trash.`);
        } else {
          addLog(`${base.name} — rested, but deck is empty (no card to mill).`);
        }
        broadcastCounters(); render(); setupDropZones();
      }, base, { skipLabel: 'No' });
  },
});

// GD04-127 "Freeden II"
// [Deploy] ...Then, if there are 7 or more (Vulture) cards in your trash,
//          choose 1 enemy Unit with 2 or less AP. Destroy it.
registerCardEffect('GD04-127', {
  deploy: (card) => {
    const vultureCount = state.playerTrash.filter(c => (c.trait || '').includes('(Vulture)')).length;
    if (vultureCount < 7) return;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — 7+ (Vulture) cards in trash but no enemy Units with 2 or less AP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 2 or less AP. Destroy it.', candidates, (target) => {
      if (target) effectDestroyEnemyUnit(target);
    }, card, { mandatory: true });
  },
});

// ST09-010 "Minerva"
// [Deploy] ...Then, if it is your turn, look at the top 2 cards of your deck.
//          Return 1 to the top. Place the remaining card into your trash.
registerCardEffect('ST09-010', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const peeked = peekTopOfDeck(2);
    if (peeked.length === 0) {
      addLog(`${card.name} — deck is empty. No effect.`);
      return;
    }
    if (peeked.length === 1) {
      sendDeckEntryToTop(peeked[0].entry);
      addLog(`${card.name} — only 1 card in deck; returned ${peeked[0].card.name} to top.`);
      render(); setupDropZones();
      return;
    }
    showCardPickerModal(card.name,
      'Look at the top 2 cards. Choose 1 to return to the top of your deck. The other goes to your trash.',
      peeked.map(p => p.card),
      (chosen) => {
        if (!chosen) { render(); setupDropZones(); return; }
        const keptItem  = peeked.find(p => p.card.uid === chosen.uid);
        const milledItem = peeked.find(p => p.card.uid !== chosen.uid);
        sendDeckEntryToTop(keptItem.entry);
        milledItem.entry.state = 'trash';
        state.trashCount++;
        state.playerTrash.push(toTrashCard(milledItem.card));
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        NET.send({ type: 'DECK_MILL', card: milledItem.card });
        broadcastCardToast(`${milledItem.card.name} was sent to trash.`);
        addLog(`${card.name} — returned ${keptItem.card.name} to top; milled ${milledItem.card.name} to trash.`);
        broadcastCounters(); render(); setupDropZones();
      }, card, { mandatory: true });
  },
});

// GD04-128 "Armory One"
// [Destroyed] All players draw 1.
registerCardEffect('GD04-128', {
  destroyed: (card, ctx) => {
    if (ctx?.defenderSide === 'opp') return; // peer's client during battle — owner handles it
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Destroyed] drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Destroyed] deck empty, no draw.`);
    }
    broadcastCounters(); render();
    if (state.networked) NET.send({ type: 'FORCE_DRAW', count: 1 });
    ctx.onDone?.();
  },
});

// GD06-129 "5th Luna"
// [Destroyed] If you have any Shields in play, deploy 1 Axis((Neo Zeon)･AP0･HP5) Base token.
// Axis is its own Base Token (T-037, categoryData 'unit-base-token') — it's
// not the EX Base (EXB-002 is a separate, selectable alt-art skin for the
// player's own starting EX Base; see CARD_APPEARANCE_DEFAULTS in game.js).
// This Axis occupies the normal Base zone (state.playerBase) same as any
// other Base card that replaces a destroyed one — nothing EX-Base-specific
// about the zone itself, only its stats/art differ from a printed Base.
registerCardEffect('GD06-129', {
  destroyed: (card, ctx) => {
    if (ctx?.defenderSide === 'opp') return; // peer's client during battle — owner handles it
    if (state.playerShield <= 0) {
      addLog(`${card.name} — [Destroyed] no Shields in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    const axis = makeBaseCard('assets/images/token/T-037.webp', 'Axis');
    axis.uid = `T-037_${Date.now()}`;
    axis.rested = false;
    axis.damaged = false;
    state.playerBase = axis;
    addLog(`${card.name} — [Destroyed] Axis Base token deployed.`);
    NET.send({ type: 'DEPLOY_BASE', card: state.playerBase, counters: myCounters() });
    render(); setupDropZones();
    ctx.onDone?.();
  },
});

// ════════════════════════════════════════════════════════════════════════════
// PURPLE PILOT & COMMAND PILOT CARDS
// ════════════════════════════════════════════════════════════════════════════

// ST05-010 "Mikazuki Augus" — Pilot
// [When Paired] Choose 1 of your Units and 1 enemy Unit. Deal 1 damage to each.
registerCardEffect('ST05-010', {
  pair: (pilot, ctx) => {
    const friendly = state.playerField.filter(c => c);
    const enemies  = state.oppField.filter(c => c);
    if (friendly.length === 0 || enemies.length === 0) {
      addLog(`${pilot.name} — not enough targets. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 of your Units to deal 1 damage to.', friendly, (fTarget) => {
      if (!fTarget) { ctx.onDone?.(); return; }
      showCardPickerModal(pilot.name, 'Choose 1 enemy Unit to deal 1 damage to.', enemies, (eTarget) => {
        if (!eTarget) { ctx.onDone?.(); return; }
        dealDamageToUnit(fTarget, 1);
        dealDamageToUnit(eTarget, 1, { fromEnemy: true });
        addLog(`${pilot.name} — dealt 1 damage to ${fTarget.name} and 1 damage to ${eTarget.name}.`);
        broadcastCounters(); render(); setupDropZones();
        ctx.onDone?.();
      }, pilot, { mandatory: true });
    }, pilot, { mandatory: true });
  },
});

// ST05-011 "Akihiro Altland" — Pilot
// [During Link] When the Unit this Pilot is paired with destroys an enemy Unit
// with battle damage during your turn, choose 1 (Tekkadan) Unit card (Lv.2 or lower)
// from your trash. Add it to your hand.
registerCardEffect('ST05-011', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (!isLinkUnit(unit)) return; // [During Link]
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (c.trait || '').includes('(Tekkadan)') &&
      (parseInt(c.level) || 0) <= 2
    );
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no (Tekkadan) Unit cards (Lv.2 or lower) in trash. No effect.`);
      return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 (Tekkadan) Unit card (Lv.2 or lower) from your trash. Add it to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${pilot.name} — recovered ${target.name} from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${pilot.name} effect.`);
      glowHandCard(target.uid);
      broadcastCounters(); render(); setupDropZones();
    }, pilot, { mandatory: true });
  },
});

// GD02-096 "Desil Galette" — Pilot
// [During Link][When Paired] Choose 1 (Vagan) Unit card (Lv.2 or lower) from
// your trash. Pay its cost to deploy it.
registerCardEffect('GD02-096', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) { ctx.onDone?.(); return; } // [During Link]
    const available = state.res + state.exRes;
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (c.trait || '').includes('(Vagan)') &&
      (parseInt(c.level) || 0) <= 2 &&
      getTrashDeployCost(c) <= available
    );
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no affordable (Vagan) Unit cards (Lv.2 or lower) in trash. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 (Vagan) Unit card (Lv.2 or lower) from your trash. Pay its cost to deploy it.', candidates, (target) => {
      if (!target) { ctx.onDone?.(); return; }
      const finishDeploy = (slot) => {
        const cost = getTrashDeployCost(target);
        spendResources(cost);
        broadcastCounters();
        deployUnitFromTrash(target, slot);
        addLog(`${pilot.name} — deployed ${target.name} from trash (paid cost ${cost}).`);
        ctx.onDone?.();
      };
      const slot = findNextFieldSlot();
      if (slot !== -1) { finishDeploy(slot); return; }
      promptTrashToMakeRoom(target.name, (freedSlot) => {
        if (freedSlot === -1) { ctx.onDone?.(); return; }
        finishDeploy(freedSlot);
      }, { mandatory: true });
    }, pilot, { mandatory: true });
  },
});

// GD02-116 "Comrades Come First" — Command Pilot
// [Main] If you have 7 or more cards in your trash, choose 1 of your (Vulture) Units.
// Until end of turn, that Unit may choose active enemy Units (Lv.4 or lower) as its attack target.
registerCardEffect('GD02-116', {
  canActivate: () =>
    (state.trashCount || 0) >= 7 &&
    state.playerField.some(c => c && (c.trait || '').includes('(Vulture)')),
  main: (card, ctx) => {
    if ((state.trashCount || 0) < 7) {
      addLog(`${card.name} — fewer than 7 cards in trash. No effect.`); ctx.onDone(); return;
    }
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Vulture)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Vulture) Units in play. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your (Vulture) Units. It may attack active enemy Units (Lv.4 or lower) this turn.', candidates, (target) => {
      if (target) {
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveLv4')) target.tempKeywords.push('CanAttackActiveLv4');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveLv4' });
        addLog(`${card.name} — ${target.name} may now attack active enemy Units (Lv.4 or lower) this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD02-114 "It's Name is Ryusei-Go" — Command Pilot
// [Main/Action] Choose 1 of your damaged Units. It gets AP+2 this turn.
function gd02114Candidates() {
  return state.playerField.filter(c => c && c.damaged);
}
function gd02114Effect(card, onDone) {
  const candidates = gd02114Candidates();
  if (candidates.length === 0) {
    addLog(`${card.name} — no damaged friendly Units. No effect.`); onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 of your damaged Units. It gets AP+2 this turn.', candidates, (target) => {
    if (target) {
      applyApMod([target], 2, 'tempApMod');
      addLog(`${card.name} — ${target.name} gets AP+2 this turn.`);
      // applyApMod() mutates locally + networks FIELD_AP_MOD, but only the
      // RECEIVING peer's NET.onFieldApMod handler calls render() — the
      // caster needs its own render() or the boost never redraws locally.
      render();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD02-114', {
  canActivate: () => gd02114Candidates().length > 0,
  main:   (card, ctx) => gd02114Effect(card, ctx.onDone),
  action: (card, ctx) => gd02114Effect(card, ctx.onDone),
});

// GD02-115 "Familial Devotion" — Command Pilot
// [Main/Action] Choose 1 of your (Vulture) Units. It gets AP+2 this turn.
function gd02115Candidates() {
  return state.playerField.filter(c => c && (c.trait || '').includes('(Vulture)'));
}
function gd02115Effect(card, onDone) {
  const candidates = gd02115Candidates();
  if (candidates.length === 0) {
    addLog(`${card.name} — no (Vulture) Units in play. No effect.`); onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 of your (Vulture) Units. It gets AP+2 this turn.', candidates, (target) => {
    if (target) {
      applyApMod([target], 2, 'tempApMod');
      addLog(`${card.name} — ${target.name} gets AP+2 this turn.`);
      // See GD02-114's identical comment: applyApMod() only self-renders on
      // the RECEIVING peer via NET.onFieldApMod — the caster needs its own.
      render();
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD02-115', {
  canActivate: () => gd02115Candidates().length > 0,
  main:   (card, ctx) => gd02115Effect(card, ctx.onDone),
  action: (card, ctx) => gd02115Effect(card, ctx.onDone),
});

// GD02-094 "Garrod Ran & Tiffa Adill" — Pilot
// [When Paired] You may discard 1 card from your hand. If you do, look at the top
// 3 cards of your deck. You may add 1 (Vulture) Unit among them to your hand.
// Return the remaining cards to the bottom of your deck.
registerCardEffect('GD02-094', {
  pair: (pilot, ctx) => {
    if (state.hand.length === 0) {
      addLog(`${pilot.name} — hand is empty, cannot discard. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'You may discard 1 card from your hand to look at the top 3 cards of your deck.', [...state.hand], (discarded) => {
      if (!discarded) {
        addLog(`${pilot.name} — discard skipped. No effect.`);
        ctx.onDone?.(); return;
      }
      state.hand = state.hand.filter(c => c.uid !== discarded.uid);
      updateTempState(discarded.cardNo, discarded.copyVer, 'trash');
      state.trashCount++;
      state.playerTrash.push(toTrashCard(discarded));
      broadcastLog(`${pilot.name} — discarded ${discarded.name}.`);
      broadcastCardToast(`${discarded.name} was discarded due to ${pilot.name} effect.`);
      broadcastCounters();

      const peeked = peekTopOfDeck(3);
      if (peeked.length === 0) {
        addLog(`${pilot.name} — deck is empty.`);
        render(); setupDropZones(); ctx.onDone?.(); return;
      }
      const isVultureUnit = p => p.card.type === 'unit' && (p.card.trait || '').includes('(Vulture)');
      // Always reveal all peeked cards, even when NONE qualify — a "look at
      // the top N cards" effect should show the player what was actually
      // looked at regardless of whether anything in it was selectable (see
      // GD02-038/GD01-045's identical fix and EB01-067's original correct
      // convention). Previously this short-circuited straight to shuffling
      // them away with just a log line when no (Vulture) Unit qualified,
      // silently skipping the reveal.
      //
      // mandatory:false here (with a Skip button) rather than the old
      // mandatory:true — once 0-eligible cases reach this picker too, a
      // mandatory picker with every tile disabled and no Skip button would
      // softlock: nothing is clickable, Confirm stays disabled forever, and
      // the selection-timer auto-resolve explicitly skips disabled
      // (.bop-unselectable) tiles too, so even that can't recover it. The
      // printed text is "you MAY add" anyway, so this is also just the
      // correct rules behavior, not only a safety fix.
      if (!peeked.some(isVultureUnit)) {
        addLog(`${pilot.name} — no (Vulture) Units among the top ${peeked.length} cards.`);
      }
      const disabledIndices = peeked.map((p, i) => i).filter(i => !isVultureUnit(peeked[i]));
      showCardPickerModal(pilot.name, `Top ${peeked.length} cards revealed. Choose 1 (Vulture) Unit to add to your hand. Others return to the bottom.`, peeked.map(p => p.card), (chosen) => {
        peeked.forEach(p => {
          if (chosen && p.card.uid === chosen.uid) {
            p.entry.state = 'hand';
            state.hand.push(p.card);
            state.deckCount = state.tempDeck.filter(isInDeck).length;
            addLog(`${pilot.name} — added ${p.card.name} to hand.`);
            NET.send({ type: 'CARD_REVEAL', card: p.card, pilotName: pilot.name });
            broadcastCardToast(`${p.card.name} was added to hand due to ${pilot.name} effect.`);
            glowHandCard(p.card.uid);
          } else {
            sendDeckEntryToBottom(p.entry);
          }
        });
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        if (!chosen) addLog(`${pilot.name} — no card taken; all returned to bottom.`);
        broadcastCounters(); render(); setupDropZones(); ctx.onDone?.();
      }, pilot, { mandatory: false, skipLabel: 'Return All', disabledIndices });
    }, pilot);
  },
});

// GD02-095 "Lafter Frankland" — Pilot
// [Attack] If this Unit is damaged and is Lv.5 or lower, it gains <High-Maneuver>
// during this battle. (High-Maneuver: the opponent cannot choose a Blocker.)
registerCardEffect('GD02-095', {
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    if (!unit || !unit.damaged || (parseInt(unit.level) || 0) > 5) { ctx.onDone?.(); return; }
    unit.tempKeywords = unit.tempKeywords || [];
    if (!unit.tempKeywords.includes('High-Maneuver')) unit.tempKeywords.push('High-Maneuver');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'High-Maneuver' });
    addLog(`${pilot.name} — damaged Lv.${unit.level || '?'} unit: gains <High-Maneuver> this battle.`);
    render(); ctx.onDone?.();
  },
});

// GD02-113 "Sisterly Care" — Command Pilot
// [Main/Action] If you have 1 or more (Teiwaz) Link Units, choose 1 active enemy
// Unit with 2 or less AP. Destroy it.
function gd02113HasTeiwazLink() {
  return state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(Teiwaz)'));
}
function gd02113Candidates() {
  return state.oppField.filter(c => c && !c.rested && effectiveBp(c) <= 2);
}
function gd02113Effect(card, onDone) {
  if (!gd02113HasTeiwazLink()) {
    addLog(`${card.name} — no (Teiwaz) Link Units in play. No effect.`); onDone(); return;
  }
  const candidates = gd02113Candidates();
  if (candidates.length === 0) {
    addLog(`${card.name} — no active enemy Units with 2 or less AP. No effect.`); onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 active enemy Unit with 2 or less AP. Destroy it.', candidates, (target) => {
    if (target) effectDestroyEnemyUnit(target);
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD02-113', {
  canActivate: () => gd02113HasTeiwazLink() && gd02113Candidates().length > 0,
  main:   (card, ctx) => gd02113Effect(card, ctx.onDone),
  action: (card, ctx) => gd02113Effect(card, ctx.onDone),
});

// GD03-096 "Jamil Neate" — Pilot
// [During Link][Attack] You may discard 1 card from your hand. If you do, draw 1.
registerCardEffect('GD03-096', {
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx.onDone?.(); return; } // [During Link]
    if (state.hand.length === 0) {
      addLog(`${pilot.name} — hand is empty, cannot discard. No draw.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'You may discard 1 card from your hand. If you do, draw 1.', [...state.hand], (target) => {
      if (target) {
        state.hand = state.hand.filter(c => c.uid !== target.uid);
        updateTempState(target.cardNo, target.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(target));
        broadcastLog(`${pilot.name} — discarded ${target.name}.`);
        broadcastCardToast(`${target.name} was discarded due to ${pilot.name} effect.`);
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length > 0) {
          state.hand.push(drawn[0]);
          addLog(`${pilot.name} — drew ${drawn[0].name}.`);
        } else {
          addLog(`${pilot.name} — deck is empty, no draw.`);
        }
      } else {
        addLog(`${pilot.name} — discard skipped. No draw.`);
      }
      broadcastCounters(); render(); setupDropZones(); ctx.onDone?.();
    }, pilot);
  },
});

// GD03-097 "Wistario Afam" — Pilot
// [During Link][Once per Turn] When the Unit this Pilot is paired with destroys an
// enemy Unit with battle damage, look at the top 2 cards of your deck. Place 1 on top
// and send the other to your trash.
registerCardEffect('GD03-097', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (!isLinkUnit(unit)) return; // [During Link]
    if (pilot.passiveUsedThisTurn) return; // [Once per Turn]
    pilot.passiveUsedThisTurn = true;
    const peeked = peekTopOfDeck(2);
    if (peeked.length === 0) {
      addLog(`${pilot.name} — [Once per Turn] deck is empty. No effect.`);
      return;
    }
    if (peeked.length === 1) {
      sendDeckEntryToTop(peeked[0].entry);
      addLog(`${pilot.name} — only 1 card in deck; returned ${peeked[0].card.name} to top.`);
      broadcastCounters(); render(); setupDropZones(); return;
    }
    showCardPickerModal(pilot.name, 'Look at the top 2 cards. Choose 1 to keep on top. The other goes to your trash.', peeked.map(p => p.card), (chosen) => {
      if (!chosen) {
        sendDeckEntryToTop(peeked[0].entry);
        const milled = peeked[1];
        milled.entry.state = 'trash';
        state.trashCount++;
        state.playerTrash.push(toTrashCard(milled.card));
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        NET.send({ type: 'DECK_MILL', card: milled.card });
        broadcastCardToast(`${milled.card.name} was sent to trash.`);
        addLog(`${pilot.name} — kept ${peeked[0].card.name} on top; milled ${milled.card.name}.`);
      } else {
        const keptItem  = peeked.find(p => p.card.uid === chosen.uid);
        const milledItem = peeked.find(p => p.card.uid !== chosen.uid);
        sendDeckEntryToTop(keptItem.entry);
        milledItem.entry.state = 'trash';
        state.trashCount++;
        state.playerTrash.push(toTrashCard(milledItem.card));
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        NET.send({ type: 'DECK_MILL', card: milledItem.card });
        broadcastCardToast(`${milledItem.card.name} was sent to trash.`);
        addLog(`${pilot.name} — kept ${keptItem.card.name} on top; milled ${milledItem.card.name}.`);
      }
      broadcastCounters(); render(); setupDropZones();
    }, pilot, { mandatory: true });
  },
});

// ST07-010 "Tieria Erde" — Pilot
// [When Destroyed] If it is the opponent's turn and this Unit is a (CB) Unit, draw 1.
registerCardEffect('ST07-010', {
  destroyed: (pilot, ctx) => {
    if (!ctx || ctx.ownerSide !== 'player') return;
    if (state.myTurn) return; // only on opponent's turn
    if (!(ctx.unit?.trait || '').includes('(CB)')) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${pilot.name} — [Destroyed] opponent's turn, (CB) Unit destroyed: drew ${drawn[0].name}.`);
    } else {
      addLog(`${pilot.name} — [Destroyed] opponent's turn, (CB) Unit destroyed: deck empty, no draw.`);
    }
    broadcastCounters(); render();
  },
});

// ST07-009 "Setsuna F. Seiei" — Pilot
// [Attack] If you have 7 or more (CB) cards in your trash, all your (CB) Units
// get AP+1 this battle. Otherwise, this Unit gets AP+1 this battle.
registerCardEffect('ST07-009', {
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const cbTrashCount = state.playerTrash.filter(c => (c.trait || '').includes('(CB)')).length;
    if (cbTrashCount >= 7) {
      const cbUnits = state.playerField.filter(c => c && (c.trait || '').includes('(CB)'));
      if (cbUnits.length > 0) applyApMod(cbUnits, 1, 'tempApMod');
      addLog(`${pilot.name} — 7+ (CB) cards in trash: all (CB) Units get AP+1 this turn (${cbUnits.length} units).`);
    } else {
      applyApMod([ctx.attacker], 1, 'battleApMod');
      addLog(`${pilot.name} — fewer than 7 (CB) cards in trash: ${ctx.attacker.name} gets AP+1 this battle.`);
    }
    render(); ctx.onDone?.();
  },
});

// GD03-115 "Distant Reunion" — Command Pilot
// [Action] Choose 1 of your (X-Rounder) Units. During this battle, it cannot receive
// battle damage from enemy Units with 2 or less AP (or 5 or less AP if you are
// player level 7 or higher).
registerCardEffect('GD03-115', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(X-Rounder)')),
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(X-Rounder)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no (X-Rounder) Units in play. No effect.`); ctx.onDone(); return;
    }
    const threshold = (state.playerLevel || 0) >= 7 ? 5 : 2;
    showCardPickerModal(card.name, `Choose 1 of your (X-Rounder) Units. During this battle, it can't receive battle damage from enemy Units with ${threshold} or less AP.`, candidates, (target) => {
      if (target) {
        const kw = `BattleImmunityFromAp${threshold}`;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes(kw)) target.tempKeywords.push(kw);
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: kw });
        addLog(`${card.name} — ${target.name} is immune to battle damage from enemies with ${threshold} or less AP this battle.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD03-094 "Zeheart Galette" — Pilot
// [When Paired] Mill the top 2 cards of your deck. If any of the milled cards is a
// (Vagan) card, choose 1 enemy Unit. It gets AP-2 this turn.
registerCardEffect('GD03-094', {
  pair: (pilot, ctx) => {
    const peeked = peekTopOfDeck(2);
    if (peeked.length === 0) {
      addLog(`${pilot.name} — deck is empty, nothing to mill.`);
      ctx.onDone?.(); return;
    }
    let milledVagan = false;
    peeked.forEach(p => {
      p.entry.state = 'trash';
      state.trashCount++;
      state.playerTrash.push(toTrashCard(p.card));
      if ((p.card.trait || '').includes('(Vagan)')) milledVagan = true;
      NET.send({ type: 'DECK_MILL', card: p.card });
      broadcastCardToast(`${p.card.name} was sent to trash.`);
    });
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    addLog(`${pilot.name} — milled ${peeked.map(p => p.card.name).join(', ')}.${milledVagan ? ' (Vagan) card milled!' : ''}`);
    broadcastCounters(); render(); setupDropZones();
    if (!milledVagan) { ctx.onDone?.(); return; }
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) {
      addLog(`${pilot.name} — no enemy Units to debuff. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'A (Vagan) card was milled! Choose 1 enemy Unit. It gets AP-2 this turn.', enemies, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${pilot.name} — ${target.name} gets AP-2 this turn.`);
        // applyApMod() mutates the target locally and networks it via
        // FIELD_AP_MOD, but only the RECEIVING peer's NET.onFieldApMod
        // handler calls render() — the caster's own client needs its own
        // render() here, or the debuff is applied but never redrawn until
        // some unrelated re-render happens to paint over it.
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, pilot, { mandatory: true });
  },
});

// GD03-095 "Azee Gurumin" — Pilot
// [Passive][Once per Turn] When the Unit paired with this Pilot receives effect
// damage, choose 1 enemy Unit. It gets AP-1 this turn.
// Fires via onUnitTakesEffectDamage hook in dealDamageToUnit (game.js).
registerCardEffect('GD03-095', {
  onUnitTakesEffectDamage: (pilot, unit) => {
    if (pilot.passiveUsedThisTurn) return; // [Once per Turn]
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) return;
    pilot.passiveUsedThisTurn = true;
    showCardPickerModal(pilot.name, '[Once per Turn] Choose 1 enemy Unit. It gets AP-1 this turn.', enemies, (target) => {
      if (target) applyApMod([target], -1, 'tempApMod');
      render(); setupDropZones();
    }, pilot, { mandatory: true });
  },
});

// GD04-093 "Rey Za Burrel" — Pilot
// [During Link][When Paired] Choose 1 of your (ZAFT) Link Units. The next time
// it receives damage this turn, reduce that damage by 2.
// Sets nextDamageReduction = 2 on the chosen unit; synced via FIELD_AP_MOD.
// Consumed in dealDamageToUnit (effect damage) and runDamageStep (battle damage).
registerCardEffect('GD04-093', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) { ctx.onDone?.(); return; } // [During Link]
    const candidates = state.playerField.filter(c =>
      c && isLinkUnit(c) && (c.trait || '').includes('(ZAFT)')
    );
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no (ZAFT) Link Units in play. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 of your (ZAFT) Link Units. The next damage it receives this turn is reduced by 2.', candidates, (target) => {
      if (target) {
        target.nextDamageReduction = (target.nextDamageReduction || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'nextDamageReduction' });
        addLog(`${pilot.name} — ${target.name}: next damage this turn reduced by 2.`);
        render(); setupDropZones();
      }
      ctx.onDone?.();
    }, pilot, { mandatory: true });
  },
});

// GD04-116 "Reliable Big Brother" — Command Pilot
// [Main] Mill the top 2 cards of your deck. For each (Minerva Squad) card among
// them, choose 1 enemy Unit with 4 or less AP. Deal X damage to it, where X is
// the number of (Minerva Squad) cards milled.
registerCardEffect('GD04-116', {
  canActivate: () => state.oppField.some(c => c && effectiveBp(c) <= 4),
  main: (card, ctx) => {
    const peeked = peekTopOfDeck(2);
    if (peeked.length === 0) {
      addLog(`${card.name} — deck is empty, nothing to mill.`); ctx.onDone(); return;
    }
    let minervaMilled = 0;
    peeked.forEach(p => {
      p.entry.state = 'trash';
      state.trashCount++;
      state.playerTrash.push(toTrashCard(p.card));
      if ((p.card.trait || '').includes('(Minerva Squad)')) minervaMilled++;
      NET.send({ type: 'DECK_MILL', card: p.card });
      broadcastCardToast(`${p.card.name} was sent to trash.`);
    });
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    addLog(`${card.name} — milled ${peeked.map(p => p.card.name).join(', ')}. ${minervaMilled} (Minerva Squad) card(s) milled.`);
    broadcastCounters(); render(); setupDropZones();
    if (minervaMilled === 0) { ctx.onDone(); return; }
    const enemies = state.oppField.filter(c => c && effectiveBp(c) <= 4);
    if (enemies.length === 0) {
      addLog(`${card.name} — no enemy Units with 4 or less AP. No damage dealt.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, `Choose 1 enemy Unit with 4 or less AP. Deal ${minervaMilled} damage to it.`, enemies, (target) => {
      if (target) {
        dealDamageToUnit(target, minervaMilled, { fromEnemy: true });
        addLog(`${card.name} — dealt ${minervaMilled} damage to ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ST09-008 "Shinn Asuka" — Pilot
// [Attack] If this is a (Minerva Squad) Unit, set 1 of your Resources as active.
registerCardEffect('ST09-008', {
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!(ctx.attacker?.trait || '').includes('(Minerva Squad)')) { ctx.onDone?.(); return; }
    if (state.res >= state.resBase) {
      addLog(`${pilot.name} — all Resources already active. No effect.`);
      ctx.onDone?.(); return;
    }
    state.res = Math.min(state.res + 1, state.resBase);
    state.resourceSetActiveThisTurn = true;
    broadcastCounters();
    addLog(`${pilot.name} — (Minerva Squad) Unit is attacking: set 1 Resource as active.`);
    render(); ctx.onDone?.();
  },
});

// GD04-096 "Ennil El" — Pilot
// [During Link][Attack] During this turn, when this Unit deals battle damage to
// an enemy Unit (Lv.5 or lower), destroy that enemy Unit.
registerCardEffect('GD04-096', {
  attack: (pilot, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx.onDone?.(); return; } // [During Link]
    ctx.attacker.tempDestroyOnHit = 5;
    addLog(`${pilot.name} — ${ctx.attacker.name}: when it deals battle damage to a Lv.5 or lower enemy this turn, that enemy is destroyed.`);
    render(); ctx.onDone?.();
  },
});

// GD04-095 "Lunamaria Hawke" — Pilot
// [During Link][When Paired] Choose 1 of your other (Minerva Squad) Units.
// During this turn, battle damage it would receive is instead dealt to this Unit.
// (The chosen Unit still deals counter-damage to the attacker normally.)
// Uses BDProxy_<uid> tempKeyword on the chosen unit; detected in runDamageStep.
registerCardEffect('GD04-095', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) { ctx.onDone?.(); return; } // [During Link]
    const lunaUnit = ctx.unit;
    const candidates = state.playerField.filter(c =>
      c && c !== lunaUnit && (c.trait || '').includes('(Minerva Squad)')
    );
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no other (Minerva Squad) Units to protect. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 of your other (Minerva Squad) Units. This turn, battle damage it would receive is dealt to this Unit instead.', candidates, (target) => {
      if (target) {
        const proxyKw = `BDProxy_${lunaUnit.uid}`;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.some(kw => kw.startsWith('BDProxy_'))) target.tempKeywords.push(proxyKw);
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: proxyKw });
        addLog(`${pilot.name} — ${lunaUnit.name} will take battle damage in place of ${target.name} this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone?.();
    }, pilot, { mandatory: true });
  },
});

// GD04-094 "Pala Sys" — Pilot
// [During Link][When Paired] Choose 1 purple Unit card with <Suppression> from
// your trash. Add it to your hand.
registerCardEffect('GD04-094', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) { ctx.onDone?.(); return; } // [During Link]
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (c.color || '').toLowerCase() === 'purple' &&
      hasNativeKeyword(c, 'Suppression')
    );
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no purple Unit cards with <Suppression> in trash. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 purple Unit card with <Suppression> from your trash. Add it to your hand.', candidates, (target) => {
      if (target) {
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${pilot.name} — recovered ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${pilot.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone?.();
    }, pilot, { mandatory: true });
  },
});

// ── Purple Unit Cards ──

// GD02-053 "Gundam X" — Lv7 Unit
// [During Link] During your turn, while 7+ in trash, all your other (Vulture) Units get AP+2.
registerCardEffect('GD02-053', {
  globalUnitApBonus: (targetCard, sourceCard) => {
    // Use field-position check so both clients agree on "owner's turn" and
    // use the correct trash count (owner's trash, not local player's trash).
    const ownersField = state.playerField.some(c => c && c.uid === sourceCard.uid);
    if (ownersField ? !state.myTurn : state.myTurn) return 0;
    if (!isLinkUnit(sourceCard)) return 0;
    if (targetCard.uid === sourceCard.uid) return 0;
    if (!(targetCard.trait || '').includes('(Vulture)')) return 0;
    const trashCount = ownersField ? (state.trashCount || 0) : (state.oppTrashCount || 0);
    if (trashCount < 7) return 0;
    return 2;
  },
});

// GD02-054 "Gundam Barbatos 1st Form" — Lv3 Unit
// [Attack] If this Unit is damaged, draw 1.
registerCardEffect('GD02-054', {
  attack: (card, ctx) => {
    if (ctx.attackerSide !== 'player') { ctx.onDone(); return; }
    if (!card.damaged) { ctx.onDone(); return; }
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — damaged: drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — damaged: deck empty, no draw.`);
    }
    broadcastCounters(); render();
    ctx.onDone();
  },
});

// GD02-055 "Gundam Gusion Rebake" — Lv5 Unit
// [Deploy] Choose 1 of your Units and 1 enemy Unit. Deal 1 damage to them.
registerCardEffect('GD02-055', {
  deploy: (card) => {
    const friendly = state.playerField.filter(c => c);
    const enemies  = state.oppField.filter(c => c);
    if (friendly.length === 0 || enemies.length === 0) {
      addLog(`${card.name} — not enough targets. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your Units to deal 1 damage to.', friendly, (fTarget) => {
      if (!fTarget) return;
      showCardPickerModal(card.name, 'Choose 1 enemy Unit to deal 1 damage to.', enemies, (eTarget) => {
        if (!eTarget) return;
        dealDamageToUnit(fTarget, 1);
        dealDamageToUnit(eTarget, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${fTarget.name} and 1 damage to ${eTarget.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }, card, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// GD02-056 "Gundam X" — Lv4 Unit
// [During Pair·(Vulture) Pilot][Destroyed] Choose 1 (Vulture) Unit card Lv5+ from trash. Add to hand.
registerCardEffect('GD02-056', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    if (!card.pilot || !(card.pilot.trait || '').includes('(Vulture)')) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (!ownerSide || ownerSide !== 'player') return;
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' && (c.trait || '').includes('(Vulture)') && (parseInt(c.level) || 0) >= 5
    );
    if (candidates.length === 0) { addLog(`${card.name} — no (Vulture) Unit cards Lv.5 or higher in trash.`); return; }
    showCardPickerModal(card.name, 'Choose 1 (Vulture) Unit card (Lv.5 or higher) from your trash. Add it to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
      glowHandCard(target.uid);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// GD02-057 "Zedas" — Lv5 Unit
// [During Pair][Attack] You may choose 1 of your other Units. Destroy it.
// If you do, choose 1 enemy Unit Lv4-. Deal 2 damage to it.
registerCardEffect('GD02-057', {
  attack: (card, ctx) => {
    if (ctx.attackerSide !== 'player') { ctx.onDone(); return; }
    if (!card.pilot) { ctx.onDone(); return; } // [During Pair]
    const others = state.playerField.filter(c => c && c.uid !== card.uid);
    if (others.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'You may choose 1 of your other Units to destroy.', others, (sacrifice) => {
      if (!sacrifice) { ctx.onDone(); return; }
      // Destroy the chosen unit
      const sIdx = state.playerField.findIndex(c => c && c.uid === sacrifice.uid);
      if (sIdx !== -1) state.playerField[sIdx] = null;
      const isToken = isUnitToken(sacrifice);
      if (!isToken) { state.trashCount++; state.playerTrash.push(toTrashCard(sacrifice)); updateTempState(sacrifice.cardNo, sacrifice.copyVer, 'trash'); }
      if (sacrifice.pilot) { state.trashCount++; state.playerTrash.push(toTrashCard(sacrifice.pilot)); updateTempState(sacrifice.pilot.cardNo, sacrifice.pilot.copyVer, 'trash'); }
      NET.send({ type: 'FIELD_HP_MOD', uids: [sacrifice.uid], delta: -99 });
      addLog(`${card.name} — ${sacrifice.name} destroyed.`);
      broadcastCounters(); render(); setupDropZones();
      // Now pick enemy Lv4- and deal 2 damage
      const enemies = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
      if (enemies.length === 0) { addLog(`${card.name} — no enemy Units Lv.4 or lower.`); ctx.onDone(); return; }
      showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.4 or lower). Deal 2 damage to it.', enemies, (eTarget) => {
        if (eTarget) { dealDamageToUnit(eTarget, 2, { fromEnemy: true }); broadcastCounters(); render(); setupDropZones(); }
        ctx.onDone();
      }, card, { mandatory: true });
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// GD02-058 "Ryusei-Go (Graze Custom II)" — Lv3 Unit
// [Deploy] Choose 1 of your Units. Deal 1 damage to it. If you do, draw 1. Then, discard 1.
registerCardEffect('GD02-058', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — no friendly Units. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 of your Units. Deal 1 damage to it.', candidates, (target) => {
      if (!target) return;
      dealDamageToUnit(target, 1);
      addLog(`${card.name} — dealt 1 damage to ${target.name}.`);
      const drawn = drawCardsFromTempDeck(1, true);
      if (drawn.length > 0) {
        state.hand.push(drawn[0]);
        addLog(`${card.name} — drew ${drawn[0].name}.`);
        broadcastCounters(); render();
        if (state.hand.length === 0) { ctx?.onDone?.(); return; }
        showCardPickerModal(card.name, 'Discard 1 card from your hand.', [...state.hand], (discard) => {
          if (discard) {
            state.hand = state.hand.filter(c => c.uid !== discard.uid);
            updateTempState(discard.cardNo, discard.copyVer, 'trash');
            state.trashCount++;
            state.playerTrash.push(toTrashCard(discard));
            broadcastLog(`${card.name} — discarded ${discard.name}.`);
            broadcastCardToast(`${discard.name} was discarded due to ${card.name} effect.`);
          }
          broadcastCounters(); render(); setupDropZones();
        }, card, { mandatory: true });
      } else {
        addLog(`${card.name} — deck empty, no draw. No discard required.`);
        broadcastCounters(); render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// GD02-060 "Gundam Leopard" — Lv5 Unit
// [Deploy] If 7+ in trash, choose 1 enemy Unit Lv4-. Rest it.
registerCardEffect('GD02-060', {
  deploy: (card) => {
    if ((state.trashCount || 0) < 7) { addLog(`${card.name} — fewer than 7 cards in trash. No effect.`); return; }
    const candidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) { addLog(`${card.name} — no active enemy Units Lv.4 or lower. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.4 or lower) to rest.', candidates, (target) => {
      if (!target) return;
      setRested(target, true);
      addLog(`${card.name} — ${target.name} is rested.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// GD02-061 "Hyakuri" — Lv4 Unit
// [When Paired·Purple Pilot] If 3+ (Teiwaz)/(Tekkadan) in trash, choose 1 enemy Unit with 3- AP. Rest it.
registerCardEffect('GD02-061', {
  pair: (card, ctx) => {
    if (!card.pilot || (card.pilot.color || '').toLowerCase() !== 'purple') return;
    const traitCount = state.playerTrash.filter(c =>
      (c.trait || '').includes('(Teiwaz)') || (c.trait || '').includes('(Tekkadan)')
    ).length;
    if (traitCount < 3) { addLog(`${card.name} — fewer than 3 (Teiwaz)/(Tekkadan) cards in trash (${traitCount}). No effect.`); return; }
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveBp(c) <= 3);
    if (candidates.length === 0) { addLog(`${card.name} — no active enemy Units with 3 or less AP. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (3 or less AP) to rest.', candidates, (target) => {
      if (!target) return;
      setRested(target, true);
      addLog(`${card.name} — ${target.name} is rested.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// GD02-064 "Gundam Leopard" — Lv5 Unit
// During your turn, while 7+ in trash, this Unit can't receive effect damage from enemy Commands.
// Approximated as immunity to all enemy effect damage during the player's turn with 7+ trash.
registerCardEffect('GD02-064', {
  effectDamageImmune: () => state.myTurn && (state.trashCount || 0) >= 7,
});

// GD02-066 "Gafran" — Lv2 Unit
// This Unit can't choose the enemy player as its attack target.
registerCardEffect('GD02-066', {
  cantAttackNonUnit: () => true,
});

// GD02-068 "Gundam Barbatos 3rd Form" — Lv4 Unit
// [Deploy] Deal 2 damage to this Unit.
registerCardEffect('GD02-068', {
  deploy: (card) => {
    dealDamageToUnit(card, 2);
    addLog(`${card.name} — dealt 2 damage to itself.`);
    broadcastCounters(); render(); setupDropZones();
  },
});

// GD03-049 "Gundam Exia (Trans-Am)" — Lv7 Unit
// [Suppression] (native)
// When this Unit destroys an enemy shield area card with battle damage,
// if 10+ (CB) cards in trash, choose 1 enemy Unit with the lowest HP. Destroy it.
registerCardEffect('GD03-049', {
  onAttackDestroysShield: (card, shieldsToRemove) => {
    const cbCount = state.playerTrash.filter(c => (c.trait || '').includes('(CB)')).length;
    if (cbCount < 10) return;
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) return;
    const lowestHp = Math.min(...enemies.map(c => effectiveHp(c)));
    const candidates = enemies.filter(c => effectiveHp(c) === lowestHp);
    const doDestroy = (target) => {
      if (!target) return;
      effectDestroyEnemyUnit(target);
      addLog(`${card.name} — ${target.name} destroyed (lowest HP: ${lowestHp}).`);
    };
    if (candidates.length === 1) { doDestroy(candidates[0]); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with the lowest HP to destroy.', candidates, doDestroy, card, { mandatory: true });
  },
});

// GD03-050 "Gundam Barbatos Lupus" — Lv7 Unit
// [Activate·Main] Choose 3 (Tekkadan)/(Teiwaz) Unit cards from your trash. Exile them. If you do, choose 1 enemy Unit. Deal 2 damage to it.
function gd03050Candidates() {
  return state.playerTrash.filter(c =>
    c.type === 'unit' && ((c.trait || '').includes('(Tekkadan)') || (c.trait || '').includes('(Teiwaz)'))
  );
}
registerCardEffect('GD03-050', {
  canActivate: () => gd03050Candidates().length >= 3 && state.oppField.some(c => c),
  activate: (card, ctx) => {
    const candidates = gd03050Candidates();
    if (candidates.length < 3) { addLog(`${card.name} — fewer than 3 (Tekkadan)/(Teiwaz) Unit cards in trash.`); ctx.onDone(); return; }
    showCardPickerModalMulti(card.name, 'Choose 3 (Tekkadan)/(Teiwaz) Unit cards from your trash to exile.', candidates,
      { min: 3, max: 3, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 3) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        broadcastCounters(); render();
        const enemies = state.oppField.filter(c => c);
        if (enemies.length === 0) { ctx.onDone(); return; }
        showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 2 damage to it.', enemies, (eTarget) => {
          if (eTarget) { dealDamageToUnit(eTarget, 2, { fromEnemy: true }); broadcastCounters(); render(); setupDropZones(); }
          ctx.onDone();
        }, card, { mandatory: true });
      }, card
    );
  },
});

// GD03-051 "Gundam X Divider" — Lv6 Unit
// [When Linked] You may choose 1 Unit card Lv4- from your trash. Pay its cost to deploy it.
registerCardEffect('GD03-051', {
  pair: (card, ctx) => {
    if (!isLinkUnit(card)) return;
    const slot = findNextFieldSlot();
    const available = state.res + state.exRes;
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (parseInt(c.level) || 0) <= 4 &&
      getTrashDeployCost(c) <= available &&
      slot !== -1
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no affordable Unit cards (Lv.4 or lower) in trash, or field is full. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'You may choose 1 Unit card (Lv.4 or lower) from your trash. Pay its cost to deploy it.', candidates, (target) => {
      if (!target) return;
      const cost = getTrashDeployCost(target);
      spendResources(cost);
      broadcastCounters();
      deployUnitFromTrash(target, findNextFieldSlot());
      addLog(`${card.name} — deployed ${target.name} from trash (paid cost ${cost}).`);
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// GD03-052 "Gundam Virtue" — Lv5 Unit
// [Activate·Main]<Support 2> (Rest this Unit. 1 other friendly Unit gets AP+2 during this turn.)
// When this Unit deals battle damage to an enemy Unit Lv5-, if (CB) Pilot in play, destroy that enemy Unit.
registerCardEffect('GD03-052', {
  canActivate: (card) => !card.rested && state.playerField.some(c => c && c.uid !== card.uid),
  activate: (card, ctx) => {
    setRested(card, true);
    addLog(`${card.name} — rested as cost.`);
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    if (candidates.length === 0) { addLog(`${card.name} — no other friendly Units.`); ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 other friendly Unit. It gets AP+2 during this turn.', candidates, (target) => {
      if (target) { applyApMod([target], 2, 'tempApMod'); addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`); broadcastCounters(); render(); }
      ctx.onDone();
    }, card, { mandatory: true });
  },
  passiveDestroyOnHitLevel: (card, defender) => {
    const hasCBPilot = state.playerField.some(u => u && u.pilot && (u.pilot.trait || '').includes('(CB)'));
    if (!hasCBPilot) return -1;
    return (parseInt(defender?.level) || 0) <= 5 ? 5 : -1;
  },
});

// GD03-053 "Gundam Gusion Rebake Full City" — Lv6 Unit
// [Blocker] (native)
// [During Pair][Once per Turn] During your turn, when a (Tekkadan)/(Teiwaz) Unit receives effect damage,
// choose 1 enemy Unit Lv4-. Rest it.
registerCardEffect('GD03-053', {
  onAnyFriendlyUnitTakesEffectDamage: (card, target) => {
    if (!state.myTurn) return;
    if (!card.pilot) return; // [During Pair]
    if (card.passiveUsedThisTurn) return;
    const trait = target.trait || '';
    if (!trait.includes('(Tekkadan)') && !trait.includes('(Teiwaz)')) return;
    card.passiveUsedThisTurn = true;
    const candidates = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) { addLog(`${card.name} — no active enemy Units Lv.4 or lower to rest.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.4 or lower) to rest.', candidates, (chosen) => {
      if (!chosen) return;
      setRested(chosen, true);
      addLog(`${card.name} — ${target.name} took effect damage; ${chosen.name} is rested.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// GD03-054 "Zeydra" — Lv6 Unit
// [High-Maneuver] (native)
// [When Paired·(X-Rounder) Pilot] You may exile 4 (Vagan) from trash. If so, destroy enemy Lv4-.
registerCardEffect('GD03-054', {
  pair: (card, ctx) => {
    if (!card.pilot || !(card.pilot.trait || '').includes('(X-Rounder)')) return;
    const vaganCards = state.playerTrash.filter(c => (c.trait || '').includes('(Vagan)'));
    if (vaganCards.length < 4) { addLog(`${card.name} — fewer than 4 (Vagan) cards in trash (${vaganCards.length}). No effect.`); return; }
    const enemies = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (enemies.length === 0) { addLog(`${card.name} — no enemy Units Lv.4 or lower. No effect.`); return; }
    showCardPickerModalMulti(card.name, 'You may exile 4 (Vagan) cards from your trash to destroy 1 enemy Unit (Lv.4 or lower).', vaganCards,
      { min: 0, max: 4, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length < 4) return;
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        broadcastCounters(); render();
        showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.4 or lower) to destroy.', enemies, (eTarget) => {
          if (!eTarget) return;
          effectDestroyEnemyUnit(eTarget);
          addLog(`${card.name} — ${eTarget.name} destroyed.`);
        }, card, { mandatory: true });
      }, card
    );
  },
});

// GD03-055 "Gundam Hajiroboshi (2nd Form)" — Lv4 Unit
// [When Paired·Purple Pilot] Choose 1 enemy Unit Lv2-. Destroy it.
registerCardEffect('GD03-055', {
  pair: (card, ctx) => {
    if (!card.pilot || (card.pilot.color || '').toLowerCase() !== 'purple') return;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.2 or lower) to destroy.', candidates, (target) => {
      if (!target) return;
      effectDestroyEnemyUnit(target);
      addLog(`${card.name} — ${target.name} destroyed.`);
    }, card, { mandatory: true });
  },
});

// GD03-056 "Gundam Barbatos Adapt" — Lv4 Unit
// [Deploy] Choose 1 of your Units and 1 enemy Unit. Deal 1 damage to them.
registerCardEffect('GD03-056', {
  deploy: (card) => {
    const friendly = state.playerField.filter(c => c);
    const enemies  = state.oppField.filter(c => c);
    if (friendly.length === 0 || enemies.length === 0) { addLog(`${card.name} — not enough targets. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 of your Units to deal 1 damage to.', friendly, (fTarget) => {
      if (!fTarget) return;
      showCardPickerModal(card.name, 'Choose 1 enemy Unit to deal 1 damage to.', enemies, (eTarget) => {
        if (!eTarget) return;
        dealDamageToUnit(fTarget, 1);
        dealDamageToUnit(eTarget, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${fTarget.name} and 1 damage to ${eTarget.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }, card, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// GD03-058 "Farsia" — Lv2 Unit
// This card in your trash gets cost -1.
registerCardEffect('GD03-058', {
  trashDeployCostMod: () => -1,
});

// GD03-059 "Zedas R" — Lv4 Unit
// [Attack] You may choose 1 (Vagan) card from your trash. Exile it. If you do, choose 1 of your (Vagan) Units. It gets AP+2 during this turn.
registerCardEffect('GD03-059', {
  attack: (card, ctx) => {
    if (ctx.attackerSide !== 'player') { ctx.onDone(); return; }
    const vaganTrash = state.playerTrash.filter(c => (c.trait || '').includes('(Vagan)'));
    if (vaganTrash.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'You may exile 1 (Vagan) card from your trash.', vaganTrash, (exileTarget) => {
      if (!exileTarget) { ctx.onDone(); return; }
      exileCardsFromTrash([exileTarget]);
      addLog(`${card.name} — exiled ${exileTarget.name} from trash.`);
      broadcastCounters(); render();
      const vaganUnits = state.playerField.filter(c => c && (c.trait || '').includes('(Vagan)'));
      if (vaganUnits.length === 0) { addLog(`${card.name} — no (Vagan) Units on field.`); ctx.onDone(); return; }
      showCardPickerModal(card.name, 'Choose 1 of your (Vagan) Units. It gets AP+2 during this turn.', vaganUnits, (bufTarget) => {
        if (bufTarget) { applyApMod([bufTarget], 2, 'tempApMod'); addLog(`${card.name} — ${bufTarget.name} gets AP+2 during this turn.`); broadcastCounters(); render(); }
        ctx.onDone();
      }, card, { mandatory: true });
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// GD03-060 "CGS Mobile Worker (Commander Type)" — Lv2 Unit
// [Once per Turn] During your turn, when this Unit receives effect damage, deploy 1 rested CGS Mobile Worker token.
registerCardEffect('GD03-060', {
  onSelfTakesEffectDamage: (card) => {
    if (!state.myTurn) return;
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const token = deployTokenToField('T-015');
    if (!token) { addLog(`${card.name} — field is full, cannot deploy CGS Mobile Worker token.`); return; }
    setRested(token, true);
    addLog(`${card.name} — CGS Mobile Worker token deployed rested.`);
    broadcastCounters(); render(); setupDropZones();
  },
});

// GD03-061 "Gundam Barbatos 6th Form" — Lv6 Unit
// While this Unit has 1 HP, it gains <Repair 3>.
registerCardEffect('GD03-061', {
  passiveKeywords: (card) => {
    if (effectiveHp(card) === 1) return ['Repair 3'];
    return [];
  },
});

// GD03-062 "GX-Bit" — Lv4 Unit
// [Deploy] If you deploy this Unit from your trash, choose 1 enemy Unit with 4- AP. Deal 2 damage to it.
registerCardEffect('GD03-062', {
  deploy: (card, ctx) => {
    if (!ctx?.fromTrash) return;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 4);
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units with 4 or less AP. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (4 or less AP). Deal 2 damage to it.', candidates, (target) => {
      if (!target) return;
      dealDamageToUnit(target, 2, { fromEnemy: true });
      addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// GD03-064 "Defurse" — Lv5 Unit
// [Deploy] You may choose 1 (X-Rounder) card from your trash and add it to your hand. If you do, discard 1.
registerCardEffect('GD03-064', {
  deploy: (card) => {
    const candidates = state.playerTrash.filter(c => (c.trait || '').includes('(X-Rounder)'));
    if (candidates.length === 0) { addLog(`${card.name} — no (X-Rounder) cards in trash. No effect.`); return; }
    showCardPickerModal(card.name, 'You may choose 1 (X-Rounder) card from your trash to add to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
      glowHandCard(target.uid);
      broadcastCounters(); render();
      if (state.hand.length === 0) { setupDropZones(); return; }
      showCardPickerModal(card.name, 'Discard 1 card from your hand.', [...state.hand], (discard) => {
        if (discard) {
          state.hand = state.hand.filter(c => c.uid !== discard.uid);
          updateTempState(discard.cardNo, discard.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(discard));
          broadcastLog(`${card.name} — discarded ${discard.name}.`);
          broadcastCardToast(`${discard.name} was discarded due to ${card.name} effect.`);
        }
        broadcastCounters(); render(); setupDropZones();
      }, card, { mandatory: true });
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// GD03-067 "Rouei" — Lv4 Unit
// [Deploy] You may choose 1 of your Units. Deal 1 damage to it. It gets AP+1 during this turn.
registerCardEffect('GD03-067', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — no friendly Units. No effect.`); return; }
    showCardPickerModal(card.name, 'You may choose 1 of your Units. Deal 1 damage to it. It gets AP+1 during this turn.', candidates, (target) => {
      if (!target) return;
      dealDamageToUnit(target, 1);
      applyApMod([target], 1, 'tempApMod');
      addLog(`${card.name} — dealt 1 damage to ${target.name}; it gets AP+1 during this turn.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// GD03-068 "Gundam Hajiroboshi" — Lv3 Unit
// While a friendly Base is in play, this Unit gains <Blocker>.
registerCardEffect('GD03-068', {
  passiveKeywords: (card, ownerSide) => {
    const base = ownerSide === 'player' ? state.playerBase : state.oppBase;
    return base ? ['Blocker'] : [];
  },
});

// GD04-049 "Gundam DX" — Lv8 Unit
// [Suppression] (native)
// [During Pair][Attack] If attacking enemy player, you may exile 7 (Vulture) from trash. If so, destroy enemy Unit/Base Lv8-.
// "Attacking the enemy player" covers Base/Shield/Direct attacks — anything
// that isn't targeting an enemy Unit — see isAttackingEnemyPlayer() in white.js.
registerCardEffect('GD04-049', {
  attack: (card, ctx) => {
    if (ctx.attackerSide !== 'player') { ctx.onDone(); return; }
    if (!card.pilot) { ctx.onDone(); return; } // [During Pair]
    if (!isAttackingEnemyPlayer(ctx)) { ctx.onDone(); return; }
    const vulture = state.playerTrash.filter(c => (c.trait || '').includes('(Vulture)'));
    if (vulture.length < 7) { ctx.onDone(); return; }
    showCardPickerModalMulti(card.name, 'You may exile 7 (Vulture) cards from your trash to destroy 1 enemy Unit/Base (Lv.8 or lower).', vulture,
      { min: 0, max: 7, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length < 7) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled 7 (Vulture) cards from trash.`);
        broadcastCounters(); render();
        const unitTargets = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 8);
        const baseTarget = state.oppBase && (parseInt(state.oppBase.level) || 0) <= 8 ? [state.oppBase] : [];
        const targets = [...unitTargets, ...baseTarget];
        if (targets.length === 0) { addLog(`${card.name} — no valid targets (Lv.8 or lower). No effect.`); ctx.onDone(); return; }
        showCardPickerModal(card.name, 'Choose 1 enemy Unit/Base (Lv.8 or lower) to destroy.', targets, (eTarget) => {
          if (eTarget) {
            if (eTarget.type === 'base' || eTarget === state.oppBase) {
              state.oppBase = null;
              addLog(`${card.name} — ${eTarget.name} (Base) destroyed.`);
              NET.send({ type: 'FIELD_HP_MOD', uids: [eTarget.uid], delta: -99 });
              broadcastCounters(); render(); setupDropZones();
            } else {
              effectDestroyEnemyUnit(eTarget);
              addLog(`${card.name} — ${eTarget.name} destroyed.`);
            }
          }
          ctx.onDone();
        }, card, { mandatory: true });
      }, card
    );
  },
});

// GD04-050 "Destiny Gundam" — Lv7 Unit
// [High-Maneuver] (native)
// [During Pair][Attack] You may choose 1 (Minerva Squad) Unit from trash. Pay its cost to deploy it.
registerCardEffect('GD04-050', {
  attack: (card, ctx) => {
    if (ctx.attackerSide !== 'player') { ctx.onDone(); return; }
    if (!card.pilot) { ctx.onDone(); return; } // [During Pair]
    const slot = findNextFieldSlot();
    const available = state.res + state.exRes;
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (c.trait || '').includes('(Minerva Squad)') &&
      getTrashDeployCost(c) <= available &&
      slot !== -1
    );
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'You may choose 1 (Minerva Squad) Unit card from your trash. Pay its cost to deploy it.', candidates, (target) => {
      if (target) {
        const cost = getTrashDeployCost(target);
        spendResources(cost);
        broadcastCounters();
        deployUnitFromTrash(target, findNextFieldSlot());
        addLog(`${card.name} — deployed ${target.name} from trash (paid cost ${cost}).`);
      }
      ctx.onDone();
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// GD04-051 "Gundam Airmaster Burst" — Lv5 Unit
// [During Pair·(Vulture) Pilot] If 7+ in trash, may choose an active enemy Unit with a keyword effect as attack target.
registerCardEffect('GD04-051', {
  canAttackThisActiveUnit: (card, target) => {
    if (!card.pilot || !(card.pilot.trait || '').includes('(Vulture)')) return false;
    if ((state.trashCount || 0) < 7) return false;
    const kwds = [...(target.keywords || []), ...(target.tempKeywords || [])];
    const gameKwds = ['Blocker', 'Suppression', 'High-Maneuver', 'Repair', 'Breach', 'Support', 'First Strike'];
    return kwds.some(kw => gameKwds.some(gk => kw.startsWith(gk)));
  },
});

// GD04-052 "Gundam Leopard Destroy" — Lv6 Unit
// [During Pair][Attack] You may choose 1 enemy Unit. Deal 2 damage to it and this Unit.
registerCardEffect('GD04-052', {
  attack: (card, ctx) => {
    if (ctx.attackerSide !== 'player') { ctx.onDone(); return; }
    if (!card.pilot) { ctx.onDone(); return; } // [During Pair]
    const enemies = state.oppField.filter(c => c);
    if (enemies.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'You may choose 1 enemy Unit. Deal 2 damage to it and to this Unit.', enemies, (eTarget) => {
      if (eTarget) {
        dealDamageToUnit(eTarget, 2, { fromEnemy: true });
        dealDamageToUnit(card, 2);
        addLog(`${card.name} — dealt 2 damage to ${eTarget.name} and 2 damage to ${card.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// GD04-053 "Rey's Blaze Zaku Phantom" — Lv4 Unit
// [During Link][Once per Turn] When this Unit receives damage from an enemy, reduce it by 1.
registerCardEffect('GD04-053', {
  effectDamageReduction: (card) => {
    if (!isLinkUnit(card)) return 0;
    if (card.passiveUsedThisTurn) return 0;
    card.passiveUsedThisTurn = true;
    addLog(`${card.name} — [During Link] incoming effect damage reduced by 1 (once per turn).`);
    return 1;
  },
  passiveUnitBattleDamageReduction: (card) => {
    if (!isLinkUnit(card)) return 0;
    if (card.passiveUsedThisTurn) return 0;
    card.passiveUsedThisTurn = true;
    addLog(`${card.name} — [During Link] incoming battle damage reduced by 1 (once per turn).`);
    return 1;
  },
});

// GD04-054 "Gundam Virtue (Trans-Am)" — Lv7 Unit
// When this Unit deals battle damage to an enemy Unit, destroy that enemy Unit.
registerCardEffect('GD04-054', {
  passiveDestroyOnHitLevel: () => 99,
});

// GD04-056 "Sword Impulse Gundam" — Lv4 Unit
// [Deploy] Deal 1 damage to this Unit. If you do, choose 1 enemy Unit with 3- AP. Rest it.
registerCardEffect('GD04-056', {
  deploy: (card) => {
    dealDamageToUnit(card, 1);
    addLog(`${card.name} — dealt 1 damage to itself.`);
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveBp(c) <= 3);
    if (candidates.length === 0) { addLog(`${card.name} — no active enemy Units with 3 or less AP.`); broadcastCounters(); render(); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (3 or less AP) to rest.', candidates, (target) => {
      if (!target) { broadcastCounters(); render(); return; }
      setRested(target, true);
      addLog(`${card.name} — ${target.name} is rested.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// GD04-057 "Gundam Nadleeh" — Lv4 Unit
// [Deploy] Choose 1 enemy Unit Lv6-. During this turn, reduce its AP by the number of "Gundam Virtue" Unit cards in your trash.
registerCardEffect('GD04-057', {
  deploy: (card) => {
    const virtueCount = state.playerTrash.filter(c =>
      (c.type === 'unit' || c.type === 'unit-token') && (c.name || '').includes('Gundam Virtue')
    ).length;
    if (virtueCount === 0) { addLog(`${card.name} — no "Gundam Virtue" Unit cards in trash. No effect.`); return; }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 6);
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units Lv.6 or lower. No effect.`); return; }
    showCardPickerModal(card.name, `Choose 1 enemy Unit (Lv.6 or lower). Reduce its AP by ${virtueCount} during this turn.`, candidates, (target) => {
      if (!target) return;
      applyApMod([target], -virtueCount, 'tempApMod');
      addLog(`${card.name} — ${target.name} gets AP-${virtueCount} during this turn (${virtueCount} Gundam Virtue in trash).`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// GD04-058 "Jamil's Gundam X" — Lv3 Unit
// [During Pair·(Vulture) Pilot][Destroyed] If it is your turn, return this Unit's paired Pilot to your hand.
registerCardEffect('GD04-058', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    if (!card.pilot || !(card.pilot.trait || '').includes('(Vulture)')) return;
    if (!state.myTurn) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (!ownerSide || ownerSide !== 'player') return;
    ctx.rescuePilot?.();
    const pilot = card.pilot;
    state.hand.push(pilot);
    updateTempState(pilot.cardNo, pilot.copyVer, 'hand');
    addLog(`${card.name} — ${pilot.name} returned to your hand.`);
    glowHandCard(pilot.uid);
    broadcastCounters(); render();
  },
});

// GD04-060 "Esperansa" — Lv3 Unit
// [Deploy] If you deploy this Unit from your trash, draw 1.
registerCardEffect('GD04-060', {
  deploy: (card, ctx) => {
    if (!ctx?.fromTrash) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — deployed from trash: drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — deployed from trash: deck empty.`);
    }
    broadcastCounters(); render(); setupDropZones();
  },
});

// GD04-061 "G-Falcon" — Lv2 Unit
// <Blocker> (native)
// This Unit can't attack while there are 6 or less cards in your trash.
registerCardEffect('GD04-061', {
  cantAttack: () => (state.trashCount || 0) <= 6,
});

// GD04-063 "GN Armor Type-E" — Lv4 Unit
// [Deploy] Choose 1 enemy Unit Lv1- or with 1- AP. Destroy it.
registerCardEffect('GD04-063', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c =>
      c && ((parseInt(c.level) || 0) <= 1 || effectiveBp(c) <= 1)
    );
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units (Lv.1 or lower, or AP 1 or less). No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.1 or lower, or AP 1 or less). Destroy it.', candidates, (target) => {
      if (!target) return;
      effectDestroyEnemyUnit(target);
      addLog(`${card.name} — ${target.name} destroyed.`);
    }, card, { mandatory: true });
  },
});

// ST05-001 "Gundam Barbatos 4th Form" — Lv6 Unit
// [Deploy] Choose 1 of your other Units. Deal 1 damage to it. It gets AP+1 during this turn.
// While this Unit is damaged, it gains <Suppression>.
registerCardEffect('ST05-001', {
  deploy: (card) => {
    const others = state.playerField.filter(c => c && c.uid !== card.uid);
    if (others.length === 0) { addLog(`${card.name} — no other friendly Units. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 of your other Units. Deal 1 damage to it. It gets AP+1 during this turn.', others, (target) => {
      if (!target) return;
      dealDamageToUnit(target, 1);
      applyApMod([target], 1, 'tempApMod');
      addLog(`${card.name} — dealt 1 damage to ${target.name}; it gets AP+1 during this turn.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
  passiveKeywords: (card) => {
    if (card.damaged) return ['Suppression'];
    return [];
  },
});

// ST05-002 "Gundam Barbatos 2nd Form" — Lv4 Unit
// While this Unit is damaged, it gets AP+2.
registerCardEffect('ST05-002', {
  passiveUnitApBonus: (card) => card.damaged ? 2 : 0,
});

// ST05-003 "CGS Mobile Worker" — Lv1 Unit
// [Activate·Main] Rest this Unit: Choose 1 of your Units. Deal 1 damage to it. It gets AP+1 during this turn.
registerCardEffect('ST05-003', {
  canActivate: (card) => !card.rested && state.playerField.some(c => c),
  activate: (card, ctx) => {
    setRested(card, true);
    addLog(`${card.name} — rested as cost.`);
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — no friendly Units.`); ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 of your Units. Deal 1 damage to it. It gets AP+1 during this turn.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 1);
        applyApMod([target], 1, 'tempApMod');
        addLog(`${card.name} — dealt 1 damage to ${target.name}; it gets AP+1 during this turn.`);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ST05-005 "Gundam Gusion Rebake" — Lv4 Unit
// [Destroyed] Choose 1 enemy Unit with 4- AP. Rest it.
registerCardEffect('ST05-005', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (!ownerSide || ownerSide !== 'player') return;
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveBp(c) <= 4);
    if (candidates.length === 0) { addLog(`${card.name} — no active enemy Units with 4 or less AP. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (4 or less AP) to rest.', candidates, (target) => {
      if (!target) return;
      setRested(target, true);
      addLog(`${card.name} — ${target.name} is rested.`);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// ST07-001 "Gundam Exia" — Lv5 Unit
// At the end of your turn, if 7+ (CB) cards in trash, choose 1 of your Resources. Set it as active.
// [When Paired] Place the top 2 cards of your deck into your trash. If you place a (CB) card, draw 1.
registerCardEffect('ST07-001', {
  endOfTurn: (card, onDone) => {
    const cbCount = state.playerTrash.filter(c => (c.trait || '').includes('(CB)')).length;
    if (cbCount < 7 || state.res >= state.resBase) { onDone(); return; }
    state.res = Math.min(state.res + 1, state.resBase);
    state.resourceSetActiveThisTurn = true;
    broadcastCounters();
    addLog(`${card.name} — end of turn: 7+ (CB) in trash; set 1 Resource as active (RES now ${state.res}).`);
    render(); onDone();
  },
  pair: (card, ctx) => {
    // Mill top 2 cards. Per-card NET.send(DECK_MILL) + broadcastCardToast,
    // matching every other mill effect's convention (e.g. GD05-052 Sazabi) —
    // previously missing both here, so the opponent's client never mirrored
    // these into their oppTrash view, and neither client got a toast.
    const milledEntries = [];
    for (let i = 0; i < 2; i++) {
      const peeked = peekTopOfDeck(1)[0];
      if (!peeked) break;
      updateTempState(peeked.card.cardNo, peeked.card.copyVer, 'trash');
      state.trashCount++;
      state.playerTrash.push(toTrashCard(peeked.card));
      peeked.entry.state = 'trash';
      milledEntries.push(peeked.card);
      NET.send({ type: 'DECK_MILL', card: peeked.card });
      broadcastCardToast(`${peeked.card.name} was sent to trash.`);
    }
    if (milledEntries.length === 0) { addLog(`${card.name} — [When Paired] deck empty, no mill.`); return; }
    addLog(`${card.name} — [When Paired] milled: ${milledEntries.map(c => c.name).join(', ')}.`);
    broadcastCounters(); render();
    const hasCB = milledEntries.some(c => (c.trait || '').includes('(CB)'));
    if (hasCB) {
      const drawn = drawCardsFromTempDeck(1, true);
      if (drawn.length > 0) {
        state.hand.push(drawn[0]);
        addLog(`${card.name} — (CB) card milled: drew ${drawn[0].name}.`);
        broadcastCounters(); render();
      }
    }
  },
});

// ST07-004 "Gundam Virtue" — Lv3 Unit
// While you have a (CB) Pilot in play, this Unit gains <Blocker>.
registerCardEffect('ST07-004', {
  passiveKeywords: (card, ownerSide) => {
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const hasCBPilot = field.some(u => u && u.pilot && (u.pilot.trait || '').includes('(CB)'));
    return hasCBPilot ? ['Blocker'] : [];
  },
});

// ST09-001 "Impulse Gundam" — Lv3 Unit
// [Activate·Main]②, return this Unit to the bottom of its owner's deck:
// Choose 1 Unit card with "Impulse Gundam" in its card name that is Lv.4 or higher from your trash. Deploy it.
registerCardEffect('ST09-001', {
  canActivate: (card) => {
    // After self-removal, occupied slots drop by 1, so check < 6 others
    const othersCount = state.playerField.filter(c => c && c.uid !== card.uid).length;
    if (othersCount >= 6) return false;
    return state.playerTrash.some(c =>
      c.type === 'unit' &&
      (c.name || '').toLowerCase().includes('impulse gundam') &&
      (parseInt(c.level) || 0) >= 4
    );
  },
  activate: (card, ctx) => {
    // Return self to bottom of deck
    const idx = state.playerField.findIndex(c => c && c.uid === card.uid);
    if (idx !== -1) {
      state.playerField[idx] = null;
      updateTempState(card.cardNo, card.copyVer, 'deck');
      const deckEntry = state.tempDeck.find(e => e.cardNo === card.cardNo && e.copyVer === card.copyVer);
      if (deckEntry) sendDeckEntryToBottom(deckEntry);
      else state.tempDeck.push({ cardNo: card.cardNo, copyVer: card.copyVer, state: 'deck' });
      if (card.pilot) {
        const pilotCard = { ...card.pilot, rested: false, damaged: false };
        updateTempState(card.pilot.cardNo, card.pilot.copyVer, 'hand');
        state.hand.push(pilotCard);
        addLog(`${card.name} — ${card.pilot.name} returned to your hand.`);
        glowHandCard(pilotCard.uid);
      }
      NET.send({ type: 'DISCARD_CARD', slot: idx });
      addLog(`${card.name} — returned to the bottom of the deck.`);
      broadcastCounters(); render(); setupDropZones();
    }
    // Deploy Impulse Gundam Lv4+ from trash
    const slot = findNextFieldSlot();
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (c.name || '').toLowerCase().includes('impulse gundam') &&
      (parseInt(c.level) || 0) >= 4 &&
      slot !== -1
    );
    if (candidates.length === 0) { addLog(`${card.name} — no Impulse Gundam Lv.4 or higher in trash.`); ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 Unit card with "Impulse Gundam" in its name (Lv.4 or higher) from your trash. Deploy it.', candidates, (target) => {
      if (target) deployUnitFromTrash(target, findNextFieldSlot());
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ST09-002 "Force Impulse Gundam" — Lv5 Unit
// [Destroyed] Choose 1 (Minerva Squad) Unit card without "Force Impulse Gundam" in its card name from your trash. Add it to your hand.
registerCardEffect('ST09-002', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (!ownerSide || ownerSide !== 'player') return;
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (c.trait || '').includes('(Minerva Squad)') &&
      !(c.name || '').includes('Force Impulse Gundam')
    );
    if (candidates.length === 0) { addLog(`${card.name} — no (Minerva Squad) Unit cards (non-Force Impulse) in trash.`); return; }
    showCardPickerModal(card.name, 'Choose 1 (Minerva Squad) Unit card (not "Force Impulse Gundam") from your trash. Add it to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
      glowHandCard(target.uid);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// ST09-006 "Sword Impulse Gundam" — Lv4 Unit
// [Deploy] If you deploy this Unit from your trash, choose 1 enemy Unit Lv3-. Destroy it.
registerCardEffect('ST09-006', {
  deploy: (card, ctx) => {
    if (!ctx?.fromTrash) return;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units Lv.3 or lower. No effect.`); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.3 or lower) to destroy.', candidates, (target) => {
      if (!target) return;
      effectDestroyEnemyUnit(target);
      addLog(`${card.name} — ${target.name} destroyed.`);
    }, card, { mandatory: true });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD05 PURPLE CARDS
// ════════════════════════════════════════════════════════════════════════════

// ─── GD05-049 · Sazabi (Lv7) ────────────────────────────────────────────────
// <Suppression> (native).
// [Attack] You may choose 1 of your Units. Destroy it. If you do, all enemy
// players each choose 1 of their non-battling Units. Destroy them.
// FORCE_DESTROY_NON_BATTLING (network.js) tells the opponent's own client to
// pick and destroy one of their own non-battling Units — only they have
// authoritative field/effect data for their own field.
registerCardEffect('GD05-049', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'You may choose 1 of your Units. Destroy it.', candidates, (target) => {
      if (!target) { ctx.onDone(); return; }
      effectDestroyOwnUnit(target, card);
      addLog(`${card.name} — destroyed ${target.name}. The enemy must destroy 1 non-battling Unit.`);
      NET.send({ type: 'FORCE_DESTROY_NON_BATTLING', excludeUid: ctx.defender?.uid });
      render(); setupDropZones();
      ctx.onDone();
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD05-050 · Gundam Exia Repair ──────────────────────────────────────────
// When this Unit deals battle damage to an enemy Unit that is Lv.4 or lower
// with no paired Pilot, destroy that enemy Unit regardless of remaining HP.
// [Destroyed] Place the top 2 cards of your deck into your trash.
// passiveDestroyOnHitLevel forces the destroy through the same runDamageStep
// branch a normal lethal hit would use — full battle damage, Breach, and the
// onUnitDestroysEnemy family of hooks all still resolve normally (satisfying
// "still counts as dealing battle damage"), while the game.js gate on
// !defender.tempImmuneToEffectDestruction ensures a Unit with effect-
// destruction immunity is NOT forced through this rider (satisfying "this
// does not count as being destroyed by battle" — the actual death here is
// conceptually an effect-destroy riding along with genuine battle damage,
// not the raw damage number bringing HP to 0).
registerCardEffect('GD05-050', {
  passiveDestroyOnHitLevel: (card, defender) => (!defender.pilot && (parseInt(defender.level) || 0) <= 4) ? 4 : -1,
  destroyed: (card, ctx) => {
    // ctx.ownerSide (not ctx.defenderSide) — unlike a Base, this Unit can be
    // destroyed either as the defender (blocked and died) or as the
    // attacker (killed by a blocker's counter-damage), and ctx.defenderSide
    // only ever describes "whichever side is defending in this battle," not
    // who owns this card — using it here incorrectly skipped the mill
    // whenever Exia Repair died as the ATTACKER (ctx.defenderSide is
    // legitimately 'opp' from its own owner's perspective in that case too).
    if (!ctx || ctx.ownerSide !== 'player') return;
    const top = peekTopOfDeck(2);
    if (top.length === 0) {
      addLog(`${card.name} — deck is empty, nothing to mill.`);
      ctx.onDone?.(); return;
    }
    top.forEach(({ entry, card: milled }) => {
      entry.state = 'trash';
      state.trashCount++;
      state.playerTrash.push(toTrashCard(milled));
      NET.send({ type: 'DECK_MILL', card: milled });
      broadcastCardToast(`${milled.name} was sent to trash.`);
      addLog(`${card.name} — [Destroyed] milled ${milled.name} to trash.`);
    });
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    broadcastCounters(); render(); setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD05-051 · Gundam Barbatos Lupus Rex ───────────────────────────────────
// Increase this Unit's AP by an amount equal to the amount of damage it has
// received (so healing/repairing lowers it back down — live-evaluated, not
// a stored value).
// At the end of your turn, you may choose 1 of your (Tekkadan) Units. Deal 1
// damage to it. Set it as active.
registerCardEffect('GD05-051', {
  passiveUnitApBonus: (card) => Math.max(0, effectiveMaxHp(card) - effectiveHp(card)),
  endOfTurn: (card, onDone) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Tekkadan)'));
    if (candidates.length === 0) { onDone(); return; }
    showCardPickerModal(
      card.name,
      'You may choose 1 of your (Tekkadan) Units. Deal 1 damage to it and set it as active.',
      candidates,
      (target) => {
        if (target) {
          dealDamageToUnit(target, 1);
          setRested(target, false);
          addLog(`${card.name} — end of turn: dealt 1 damage to ${target.name} and set it as active.`);
          render(); setupDropZones();
        }
        onDone();
      },
      card,
      { mandatory: false, skipLabel: 'Decline' }
    );
  },
});

// ─── GD05-052 · Sazabi (Lv5) ─────────────────────────────────────────────────
// [Deploy] You may choose 1 of your other Units. Destroy it. If you do,
// place the top 3 cards of your deck into your trash. Add 1 (Neo Zeon) Unit
// card you placed from your deck with this effect to your hand.
registerCardEffect('GD05-052', {
  deploy: (card) => {
    const others = state.playerField.filter(c => c && c.uid !== card.uid);
    if (others.length === 0) { addLog(`${card.name} — no other Units. No effect.`); return; }
    showCardPickerModal(card.name, 'You may choose 1 of your other Units. Destroy it.', others, (target) => {
      if (!target) return;
      effectDestroyOwnUnit(target, card);
      const milled = [];
      for (let i = 0; i < 3; i++) {
        const peeked = peekTopOfDeck(1)[0];
        if (!peeked) break;
        peeked.entry.state = 'trash';
        state.trashCount++;
        state.playerTrash.push(toTrashCard(peeked.card));
        NET.send({ type: 'DECK_MILL', card: peeked.card });
        broadcastCardToast(`${peeked.card.name} was sent to trash.`);
        milled.push(peeked.card);
      }
      state.deckCount = state.tempDeck.filter(isInDeck).length;
      addLog(`${card.name} — milled ${milled.length > 0 ? milled.map(c => c.name).join(', ') : 'nothing'} to trash.`);
      const eligible = milled.filter(c => (c.type === 'unit' || c.type === 'unit-token') && (c.trait || '').includes('(Neo Zeon)'));
      broadcastCounters(); render(); setupDropZones();
      if (eligible.length === 0) return;
      showCardPickerModal(
        card.name,
        'Choose 1 (Neo Zeon) Unit card placed into your trash with this effect. Add it to your hand.',
        eligible,
        (chosen) => {
          if (!chosen) return;
          const idx = state.playerTrash.findIndex(c => c.uid === chosen.uid);
          if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
          updateTempState(chosen.cardNo, chosen.copyVer, 'hand');
          state.hand.push(chosen);
          addLog(`${card.name} — added ${chosen.name} to hand.`);
          glowHandCard(chosen.uid);
          broadcastCounters(); render(); setupDropZones();
        },
        card,
        { mandatory: false, skipLabel: 'Decline' }
      );
    }, card, { mandatory: false, skipLabel: 'Skip' });
  },
});

// ─── GD05-053 · Quess's Jagd Doga ────────────────────────────────────────────
// [Destroyed] If this Unit is destroyed by one of your (Neo Zeon) card's
// effects, add it from your trash to your hand.
registerCardEffect('GD05-053', {
  destroyed: (card, ctx) => {
    if (ctx?.cause !== 'effect' || !ctx.sourceCard) return;
    if (!(ctx.sourceCard.trait || '').includes('(Neo Zeon)')) return;
    const idx = state.playerTrash.findIndex(c => c.uid === card.uid);
    if (idx === -1) return;
    state.playerTrash.splice(idx, 1);
    state.trashCount = Math.max(0, state.trashCount - 1);
    updateTempState(card.cardNo, card.copyVer, 'hand');
    state.hand.push(card);
    addLog(`${card.name} — destroyed by one of your (Neo Zeon) card's effects: returned to hand.`);
    glowHandCard(card.uid);
    broadcastCounters(); render(); setupDropZones();
  },
});

// ─── GD05-054 · Alpha Azieru ─────────────────────────────────────────────────
// <Blocker> (native).
// [Once per Turn] When one of your Units is destroyed by an effect, draw 1.
registerCardEffect('GD05-054', {
  onFriendlyUnitDestroyedByEffect: (card, destroyedUnit) => {
    if (card.passiveUsedThisTurn) return;
    card.passiveUsedThisTurn = true;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Once per Turn] ${destroyedUnit.name} destroyed by an effect: drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — deck empty, no draw.`);
    }
    broadcastCounters(); render(); setupDropZones();
  },
});

// ─── GD05-055 · Destiny Gundam ───────────────────────────────────────────────
// <First Strike> (native).
// [Once per Turn] When this Unit receives enemy battle damage, reduce it by 2.
registerCardEffect('GD05-055', {
  passiveUnitBattleDamageReduction: (card) => {
    if (card.passiveUsedThisTurn) return 0;
    card.passiveUsedThisTurn = true;
    return 2;
  },
});

// ─── GD05-057 · Gyunei's Jagd Doga (Lv4) ────────────────────────────────────
// [Activate･Main][Once per Turn] Choose 1 of your other Units. Destroy it.
// If you do, set this Unit as active. It can't choose the enemy player as
// its attack target during this turn.
// Once-per-Turn + Main-Phase gating handled generically by
// canActivateUnitEffect/showUnitActivateModal.
registerCardEffect('GD05-057', {
  canActivate: (card) => state.playerField.some(c => c && c.uid !== card.uid),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid);
    if (candidates.length === 0) {
      addLog(`${card.name} — no other Units. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your other Units. Destroy it.', candidates, (target) => {
      if (target) {
        effectDestroyOwnUnit(target, card);
        setRested(card, false);
        card.tempCantDirectAttack = true;
        addLog(`${card.name} — destroyed ${target.name}: set active. Can't attack the enemy player directly this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD05-059 · Gundam Barbatos Lupus ────────────────────────────────────────
// [Attack] Choose 1 of your active (Gjallarhorn) Units. Rest it. If you do,
// draw 1. This Unit gains <High-Maneuver> during this turn.
// (This Unit can't be blocked.)
registerCardEffect('GD05-059', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('High-Maneuver')) card.tempKeywords.push('High-Maneuver');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'High-Maneuver' });
    addLog(`${card.name} — gains <High-Maneuver> this turn.`);
    const candidates = state.playerField.filter(c => c && !c.rested && (c.trait || '').includes('(Gjallarhorn)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no active (Gjallarhorn) Units. No further effect.`);
      render(); ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your active (Gjallarhorn) Units. Rest it and draw 1.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        const drawn = drawCardsFromTempDeck(1, true);
        if (drawn.length > 0) {
          state.hand.push(drawn[0]);
          addLog(`${card.name} — rested ${target.name}: drew ${drawn[0].name}.`);
        }
        broadcastCounters();
      }
      render(); setupDropZones();
      ctx.onDone();
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD05-060 · Gundam Flauros (Ryusei-Go) ──────────────────────────────────
// [Deploy]/[Attack] Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.
function gd05060Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`);
    onDone?.(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 enemy Unit Lv.2 or lower. Destroy it.', candidates, (target) => {
    if (target) {
      effectDestroyEnemyUnit(target);
      addLog(`${card.name} — destroyed ${target.name}.`);
    }
    onDone?.();
  }, card, { mandatory: true });
}
registerCardEffect('GD05-060', {
  deploy: (card) => gd05060Effect(card, () => {}),
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    gd05060Effect(card, ctx.onDone);
  },
});

// ─── GD05-061 · Geara Doga ───────────────────────────────────────────────────
// While you have another (Neo Zeon) Unit in play, this Unit gains <Blocker>.
registerCardEffect('GD05-061', {
  passiveKeywords: (card, ownerSide) => {
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const hasOtherNeoZeon = field.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Neo Zeon)'));
    return hasOtherNeoZeon ? ['Blocker'] : [];
  },
});

// ─── GD05-064 · Force Impulse Gundam ────────────────────────────────────────
// [Deploy] If you deploy this Unit from your trash, choose 1 Pilot card with
// "Shinn Asuka" in its card name from your trash. Add it to your hand.
registerCardEffect('GD05-064', {
  deploy: (card, ctx) => {
    if (!ctx?.fromTrash) return;
    const candidates = state.playerTrash.filter(c => c.type === 'pilot' && (c.name || '').includes('Shinn Asuka'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no Pilot card with "Shinn Asuka" in its name in trash. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 Pilot card with "Shinn Asuka" in its name from your trash. Add it to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
      glowHandCard(target.uid);
      broadcastCounters(); render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// ─── GD05-065 · Landman Rodi ─────────────────────────────────────────────────
// [During Link] This Unit gets AP+2 during your turn.
registerCardEffect('GD05-065', {
  passiveUnitApBonus: (card, ownerSide) => {
    if (!isLinkUnit(card)) return 0;
    const isControllersTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    return isControllersTurn ? 2 : 0;
  },
});

// ─── GD05-093 · Char Aznable — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] You may choose 1 (Neo Zeon) Base card from your trash.
// Deploy it.
registerCardEffect('GD05-093', {
  pair: (pilot, ctx) => {
    if (!isLinkUnit(ctx.unit)) return;
    if (state.playerBase) return;
    const candidates = state.playerTrash.filter(c => c.type === 'base' && (c.trait || '').includes('(Neo Zeon)'));
    if (candidates.length === 0) return;
    showCardPickerModal(pilot.name, 'You may choose 1 (Neo Zeon) Base card from your trash. Deploy it.', candidates, (target) => {
      if (target) deployBaseFromTrash(target);
    }, pilot, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD05-094 · Quess Paraya — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Destroyed] Choose 1 of your (Neo Zeon) Units. During this turn, when it
// receives enemy battle damage, reduce it by 2.
registerCardEffect('GD05-094', {
  destroyed: (pilot, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Neo Zeon)'));
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no friendly (Neo Zeon) Units. No effect.`);
      return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 of your (Neo Zeon) Units. Reduce enemy battle damage to it by 2 this turn.', candidates, (target) => {
      if (target) {
        target.tempBattleDamageReduction = (target.tempBattleDamageReduction || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempBattleDamageReduction' });
        addLog(`${pilot.name} — ${target.name} will reduce enemy battle damage by 2 this turn.`);
        render();
      }
    }, pilot, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD05-095 · Gyunei Guss — Pilot ─────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit is (Neo Zeon), it gains <Blocker>.
registerCardEffect('GD05-095', {
  pilotPassiveKeywords: (pilot, unit) =>
    (unit.trait || '').includes('(Neo Zeon)') ? ['Blocker'] : [],
});

// ─── GD05-096 · Chad Chadan — Pilot ─────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Attack] You may deal 1 damage to this Unit. If you do, choose 1 of your
// other (Tekkadan) Units. It recovers 1 HP.
registerCardEffect('GD05-096', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    showCardPickerModal(
      card.name,
      'You may deal 1 damage to this Unit. If you do, choose 1 of your other (Tekkadan) Units to recover 1 HP.',
      [unit],
      (chosen) => {
        if (!chosen) { ctx.onDone(); return; }
        dealDamageToUnit(unit, 1);
        addLog(`${card.name} — dealt 1 damage to ${unit.name}.`);
        const candidates = state.playerField.filter(c => c && c.uid !== unit.uid && (c.trait || '').includes('(Tekkadan)'));
        if (candidates.length === 0) {
          addLog(`${card.name} — no other (Tekkadan) Units. No further effect.`);
          render(); setupDropZones();
          ctx.onDone();
          return;
        }
        showCardPickerModal(card.name, 'Choose 1 of your other (Tekkadan) Units. It recovers 1 HP.', candidates, (target) => {
          if (target) {
            applyHpMod([target], 1);
            addLog(`${card.name} — ${target.name} recovers 1 HP.`);
          }
          render(); setupDropZones();
          ctx.onDone();
        }, card, { mandatory: true });
      },
      card,
      { mandatory: false, skipLabel: 'Decline' }
    );
  },
});

// ─── GD05-114 · Widespread Annihilation — Command ──────────────────────────
// [Main] Destroy all Units that are Lv.4 or lower (both sides).
registerCardEffect('GD05-114', {
  canActivate: () => {
    const lv4 = (u) => u && (isUnitToken(u) ? 0 : (parseInt(u.level) || 0)) <= 4;
    return state.playerField.some(lv4) || state.oppField.some(lv4);
  },
  main: (card, ctx) => {
    const lv4 = (u) => u && (isUnitToken(u) ? 0 : (parseInt(u.level) || 0)) <= 4;
    const ownTargets = state.playerField.filter(lv4);
    const enemyTargets = state.oppField.filter(lv4);
    addLog(`${card.name} — [Main] destroying all Units Lv.4 or lower.`);
    ownTargets.forEach(u => effectDestroyOwnUnit(u, card));
    enemyTargets.forEach(u => effectDestroyEnemyUnit(u));
    render(); setupDropZones();
    ctx.onDone();
  },
});

// ─── GD05-115 · Newtype Labs Director — Command ────────────────────────────
// [Burst] Draw 1.
// [Main] Choose 1 (Neo Zeon) Pilot card from your trash. Add it to your hand.
registerCardEffect('GD05-115', {
  burst: (card) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Burst] drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Burst] deck empty, no draw.`);
    }
    broadcastCounters(); render(); setupDropZones();
  },
  canActivate: () => state.playerTrash.some(c => c.type === 'pilot' && (c.trait || '').includes('(Neo Zeon)')),
  main: (card, ctx) => {
    const candidates = state.playerTrash.filter(c => c.type === 'pilot' && (c.trait || '').includes('(Neo Zeon)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Neo Zeon) Pilot cards in trash. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Neo Zeon) Pilot card from your trash. Add it to your hand.', candidates, (target) => {
      if (target) {
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-116 · Veteran's Pride — Command Pilot ────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.
// [Pilot] Rezin Schnyder
function gd05116Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
  if (candidates.length === 0) {
    addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(card.name, 'Choose 1 enemy Unit Lv.2 or lower. Destroy it.', candidates, (target) => {
    if (target) {
      effectDestroyEnemyUnit(target);
      addLog(`${card.name} — destroyed ${target.name}.`);
    }
    onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD05-116', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 2),
  main: (card, ctx) => gd05116Effect(card, ctx.onDone),
  action: (card, ctx) => gd05116Effect(card, ctx.onDone),
});

// ─── GD05-117 · Become a Shield — Command Pilot ────────────────────────────
// [Main]/[Action] Choose 1 of your Units and 1 enemy Unit. Deal 1 damage to
// them.
// [Pilot] Ride Mass
function gd05117Effect(card, onDone) {
  const friendlies = state.playerField.filter(c => c);
  if (friendlies.length === 0) { addLog(`${card.name} — no friendly Units. No effect.`); onDone(); return; }
  const enemies = state.oppField.filter(c => c);
  if (enemies.length === 0) { addLog(`${card.name} — no enemy Units. No effect.`); onDone(); return; }
  showCardPickerModal(card.name, 'Choose 1 of your Units. Deal 1 damage to it.', friendlies, (friendly) => {
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Deal 1 damage to it.', enemies, (enemy) => {
      if (friendly) { dealDamageToUnit(friendly, 1); addLog(`${card.name} — dealt 1 damage to ${friendly.name} (friendly).`); }
      if (enemy) { dealDamageToUnit(enemy, 1, { fromEnemy: true }); addLog(`${card.name} — dealt 1 damage to ${enemy.name} (enemy).`); }
      render(); setupDropZones();
      onDone();
    }, card, { mandatory: true });
  }, card, { mandatory: true });
}
registerCardEffect('GD05-117', {
  canActivate: () => state.playerField.some(c => c) && state.oppField.some(c => c),
  main: (card, ctx) => gd05117Effect(card, ctx.onDone),
  action: (card, ctx) => gd05117Effect(card, ctx.onDone),
});

// ─── GD05-129 · Axis — Base ─────────────────────────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Activate･Main] Rest this Base: If one of your Units has been destroyed by
// one of your (Neo Zeon) card's effects during this turn, deploy 1 (Neo
// Zeon) Unit card that is Lv.3 or lower from your hand.
// state.neoZeonEffectDestroyedOwnUnitThisTurn is set by effectDestroyOwnUnit
// whenever sourceCard is (Neo Zeon); cleared each turn by
// CLEAR_TURN_EFFECTS/UNREST_ALL. "Rest this Base" cost parsed generically by
// getBaseActivationCost().
function gd05129Candidates() {
  return state.hand.filter(c =>
    (c.type === 'unit' || c.type === 'unit-token') &&
    (c.trait || '').includes('(Neo Zeon)') &&
    (parseInt(c.level) || 0) <= 3
  );
}
registerCardEffect('GD05-129', {
  canActivate: () => state.neoZeonEffectDestroyedOwnUnitThisTurn && gd05129Candidates().length > 0,
  activate: (card, ctx) => {
    if (!state.neoZeonEffectDestroyedOwnUnitThisTurn) {
      addLog(`${card.name} — no Unit destroyed by one of your (Neo Zeon) card's effects this turn. No effect.`);
      ctx.onDone(); return;
    }
    const candidates = gd05129Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Neo Zeon) Unit card Lv.3 or lower in hand. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Neo Zeon) Unit card Lv.3 or lower from your hand. Deploy it.', candidates, (target) => {
      if (!target) { ctx.onDone(); return; }
      const slot = state.playerField.findIndex(c => !c);
      if (slot !== -1) { deployUnitFromHandFree(target, slot); ctx.onDone(); return; }
      promptTrashToMakeRoom(target.name, (freedSlot) => {
        if (freedSlot === -1) { ctx.onDone(); return; }
        deployUnitFromHandFree(target, freedSlot);
        ctx.onDone();
      }, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// ─── ST11-006 · Shamblo ──────────────────────────────────────────────────────
// If another friendly (Marine) Unit is in play at the start of your
// opponent's turn, during this turn, when a friendly shield area card
// receives enemy effect damage, reduce it by 5.
// [Deploy] Choose 1 (Marine) Unit card from your trash. Add it to your hand.
// reduceBaseEffectDamage is checked live (see dealDamageToBase in game.js)
// rather than snapshotted "at the start of your opponent's turn" — the
// condition (another friendly Marine Unit in play) essentially never
// changes mid-opponent-turn in practice, so a live check reads the same as
// a snapshot without needing a new turn-start hook.
registerCardEffect('ST11-006', {
  reduceBaseEffectDamage: (base, side) => {
    const friendlyField = side === 'player' ? state.playerField : state.oppField;
    const hasAnotherMarine = friendlyField.some(c => c && c.cardNo !== base.cardNo && (c.trait || '').includes('(Marine)'));
    return hasAnotherMarine ? 5 : 0;
  },
  deploy: (card) => {
    const candidates = state.playerTrash.filter(c => c.type === 'unit' && (c.trait || '').includes('(Marine)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Marine) Unit cards in trash. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Marine) Unit card from your trash. Add it to your hand.', candidates, (target) => {
      if (target) {
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters(); render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── ST11-009 · Kapool ───────────────────────────────────────────────────────
// [Destroyed] If it is your opponent's turn and a friendly (Marine) Unit is
// in play, draw 1.
registerCardEffect('ST11-009', {
  destroyed: (card, ctx) => {
    if (!ctx || ctx.ownerSide !== 'player') return;
    if (state.myTurn) return; // must be the opponent's turn
    // Kapool is itself a (Marine) Unit and is already off state.playerField
    // by the time [Destroyed] fires (the field slot is nulled before the
    // trigger runs) — checked separately so it still counts as "a friendly
    // (Marine) Unit in play" when it was the only one.
    const hasMarine = (card.trait || '').includes('(Marine)') ||
      state.playerField.some(c => c && (c.trait || '').includes('(Marine)'));
    if (!hasMarine) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Destroyed] opponent's turn, friendly (Marine) Unit in play: drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [Destroyed] opponent's turn, friendly (Marine) Unit in play: deck empty, no draw.`);
    }
    broadcastCounters(); render();
  },
});

// ─── ST11-012 · Loni Garvey — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Choose 1 friendly (Marine) Unit. During this turn, when it
// receives battle damage from an enemy Unit, reduce it by 2.
registerCardEffect('ST11-012', {
  pair: (pilot, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Marine)'));
    if (candidates.length === 0) {
      addLog(`${pilot.name} — no friendly (Marine) Units. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(pilot.name, 'Choose 1 friendly (Marine) Unit. Reduce enemy battle damage to it by 2 this turn.', candidates, (target) => {
      if (target) {
        target.tempBattleDamageReduction = (target.tempBattleDamageReduction || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempBattleDamageReduction' });
        addLog(`${pilot.name} — ${target.name} will reduce enemy battle damage by 2 this turn.`);
        render();
      }
      ctx.onDone?.();
    }, pilot, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── ST11-015 · A Twinkle from the Abyss ─────────────────────────────────────
// [Main] Choose 1 (Marine) Unit card that is Lv.4 or lower from your trash.
// Deploy it rested.
registerCardEffect('ST11-015', {
  main: (card, ctx) => {
    const slot = findNextFieldSlot();
    const candidates = state.playerTrash.filter(c =>
      c.type === 'unit' &&
      (c.trait || '').includes('(Marine)') &&
      (parseInt(c.level) || 0) <= 4 &&
      slot !== -1
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Marine) Unit cards (Lv.4 or lower) in trash, or field is full. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Marine) Unit card (Lv.4 or lower) from your trash. Deploy it rested.', candidates, (target) => {
      if (target) deployUnitFromTrash(target, findNextFieldSlot(), { rested: true });
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── ST12-014 · Wise Leader's Pride ──────────────────────────────────────────
// [Action] Choose 1 friendly Unit. It gains the following effect during this
// battle: When this Unit destroys an enemy Unit with battle damage, choose 1
// enemy Unit with 2 or less AP. Destroy it.
registerCardEffect('ST12-014', {
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 friendly Unit. During this battle, when it destroys an enemy Unit with battle damage, choose 1 enemy Unit with 2 or less AP and destroy it.', candidates, (target) => {
      if (target) {
        target.battleOnDestroyEnemyDestroyLowAp = true;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'battleOnDestroyEnemyDestroyLowAp' });
        addLog(`${card.name} — ${target.name} gains: when it destroys an enemy Unit with battle damage this battle, destroy 1 enemy Unit with 2 or less AP.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── ST12-007 · Gyan ─────────────────────────────────────────────────────────
// [When Linked] This Unit gains <First Strike> during this turn.
registerCardEffect('ST12-007', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('First Strike')) card.tempKeywords.push('First Strike');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'First Strike' });
    addLog(`${card.name} — [When Linked] gains <First Strike> during this turn.`);
    render();
  },
});

// ─── ST12-006 · Unicorn Gundam 02 Banshee (Destroy Mode) ─────────────────────
// [During Pair][Activate･Main] Exile 4 cards in your trash from the game:
// This Unit gains <First Strike> during this turn.
// [During Pair][Activate･Action] Exile 4 cards in your trash from the game:
// This Unit gains <Suppression> during this battle.
// No RES cost on either ability — the trash-exile is the entire cost, paid
// by hand same as EB01-001/GD05-022's exile-cost abilities. "During this
// battle" is granted the same way as "during this turn" (tempKeywords,
// cleared at end of turn) — see EB01-028 for the same convention.
registerCardEffect('ST12-006', {
  canActivate: (card) => !!card.pilot && state.playerTrash.length >= 4,
  activate: (card, ctx) => {
    if (!card.pilot) { ctx.onDone(); return; }
    if (state.playerTrash.length < 4) {
      addLog(`${card.name} — need 4 cards in trash to activate. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModalMulti(
      card.name,
      'Exile 4 cards from your trash. This Unit gains <First Strike> during this turn.',
      state.playerTrash,
      { min: 4, max: 4, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 4) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        card.tempKeywords = card.tempKeywords || [];
        if (!card.tempKeywords.includes('First Strike')) card.tempKeywords.push('First Strike');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'First Strike' });
        addLog(`${card.name} — exiled 4 cards from trash. Gains <First Strike> this turn.`);
        broadcastCounters();
        render(); setupDropZones();
        ctx.onDone();
      },
      card
    );
  },
  canAction: (card) => !!card.pilot && state.playerTrash.length >= 4,
  action: (card, ctx) => {
    if (!card.pilot) { ctx.onDone(); return; }
    if (state.playerTrash.length < 4) {
      addLog(`${card.name} — need 4 cards in trash to activate. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModalMulti(
      card.name,
      'Exile 4 cards from your trash. This Unit gains <Suppression> during this battle.',
      state.playerTrash,
      { min: 4, max: 4, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 4) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        card.tempKeywords = card.tempKeywords || [];
        if (!card.tempKeywords.includes('Suppression')) card.tempKeywords.push('Suppression');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'Suppression' });
        addLog(`${card.name} — exiled 4 cards from trash. Gains <Suppression> this battle.`);
        broadcastCounters();
        render(); setupDropZones();
        ctx.onDone();
      },
      card
    );
  },
});

// ─── ST12-012 · Ple-Twelve ────────────────────────────────────────────────
// This card's name is also treated as [Marida Cruz]. (generic — parsed from
// effectData's "treated as [X]" text into pilotAliases; no code needed here.)
// [Burst] Add this card to your hand. (generic — matched by effectRaw text.)
// [When Linked] Look at the top 2 cards of your deck and return 1 to the
// top. Place the remaining card into your trash. If there is a player with
// 3 or less Shields, add the card to your hand instead of returning it to
// your deck.
// Per user clarification: "a player with 3 or less Shields" means the
// ENEMY's shield count (state.oppShield), and the redirected card goes to
// the hand of whoever controls Ple-Twelve. The card text never says
// "reveal", so — unlike lookAtTopAndPickToHand — the kept card is never
// announced to the opponent (no CARD_REVEAL/broadcastCardToast for it);
// only the milled card, which is publicly visible in the trash, is.
registerCardEffect('ST12-012', {
  pair: (pilot, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    const toHand = state.oppShield <= 3;
    const resolveKept = (keptEntry, keptCard) => {
      if (toHand) {
        keptEntry.state = 'hand';
        state.hand.push(keptCard);
        addLog(`${pilot.name} — [When Linked] enemy has ${state.oppShield} Shields: added ${keptCard.name} to hand.`);
      } else {
        sendDeckEntryToTop(keptEntry);
        addLog(`${pilot.name} — [When Linked] returned ${keptCard.name} to the top of the deck.`);
      }
    };
    const peeked = peekTopOfDeck(2);
    if (peeked.length === 0) {
      addLog(`${pilot.name} — [When Linked] deck is empty. No effect.`);
      return;
    }
    if (peeked.length === 1) {
      resolveKept(peeked[0].entry, peeked[0].card);
      state.deckCount = state.tempDeck.filter(isInDeck).length;
      broadcastCounters(); render(); setupDropZones();
      return;
    }
    showCardPickerModal(
      pilot.name,
      toHand
        ? 'Look at the top 2 cards of your deck. Choose 1 to add to your hand. The other goes to your trash.'
        : 'Look at the top 2 cards of your deck. Choose 1 to return to the top of your deck. The other goes to your trash.',
      peeked.map(p => p.card),
      (chosen) => {
        if (!chosen) { render(); setupDropZones(); return; }
        const keptItem = peeked.find(p => p.card.uid === chosen.uid);
        const milledItem = peeked.find(p => p.card.uid !== chosen.uid);
        resolveKept(keptItem.entry, keptItem.card);
        milledItem.entry.state = 'trash';
        state.trashCount++;
        state.playerTrash.push(toTrashCard(milledItem.card));
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        NET.send({ type: 'DECK_MILL', card: milledItem.card });
        broadcastCardToast(`${milledItem.card.name} was sent to trash.`);
        addLog(`${pilot.name} — [When Linked] milled ${milledItem.card.name} to trash.`);
        broadcastCounters(); render(); setupDropZones();
      },
      pilot,
      { mandatory: true }
    );
  },
});

// ─── ST12-009 · Unicorn Gundam 02 Banshee (Unicorn Mode) ───────────────────
// [Destroyed] If there is a player with 3 or less Shields, choose 1 Unit
// card that is Lv.6 or higher from your trash. Add it to your hand. If you
// do, discard 1.
// "a player" = either player (per user clarification) — checked via
// state.playerShield / state.oppShield, the plain shield-count numbers used
// by every other shield-count condition in this codebase (see ST12-012
// above for state.oppShield). Fetch+discard flow mirrors GD05-024's
// [Destroyed] handler exactly.
registerCardEffect('ST12-009', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    if (state.playerShield > 3 && state.oppShield > 3) {
      addLog(`${card.name} — [Destroyed] no player has 3 or fewer Shields. No effect.`);
      return;
    }
    const candidates = state.playerTrash.filter(c => c.type === 'unit' && (parseInt(c.level) || 0) >= 6);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Destroyed] no Lv.6+ Unit cards in trash. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 Unit card that is Lv.6 or higher from your trash. Add it to your hand.',
      candidates,
      (target) => {
        if (!target) return;
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters(); render();
        if (state.hand.length === 0) { setupDropZones(); return; }
        showCardPickerModal(card.name, 'Discard 1 card from your hand.', [...state.hand], (discard) => {
          if (discard) {
            state.hand = state.hand.filter(c => c.uid !== discard.uid);
            updateTempState(discard.cardNo, discard.copyVer, 'trash');
            state.trashCount++;
            state.playerTrash.push(toTrashCard(discard));
            broadcastLog(`${card.name} — discarded ${discard.name}.`);
            broadcastCardToast(`${discard.name} was discarded due to ${card.name} effect.`);
          }
          broadcastCounters(); render(); setupDropZones();
        }, card, { mandatory: true });
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-058 · Blast Impulse Gundam ─────────────────────────────────────────
// On the turn this Unit is deployed, it may choose a rested enemy Unit as
// its attack target and attack it.
// Attacking a rested enemy Unit is this game's normal default targeting
// rule (see canAttackActiveUnits in game.js: "by default units can only
// attack rested units") — the only thing this card actually grants is
// bypassing its own deploy-turn summoning sickness, same tempAttackDespiteSickness
// mechanism as GD05-030 Michaelis. Unlike Michaelis, the printed text has no
// "can't attack a base/shield/direct until next turn" clause, so
// tempCantAttackNonUnit is NOT set here — this Unit can make a completely
// normal attack (including base/shield/direct) on its deploy turn.
registerCardEffect('GD06-058', {
  deploy: (card) => {
    card.tempAttackDespiteSickness = true;
    addLog(`${card.name} — [Deploy] may attack this turn despite just being deployed.`);
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 batch — "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── GD06-117 · Veterans Joining Forces — Command Pilot ───────────────────
// [Action] Choose 1 rested friendly (Delaz Fleet) Unit. Change a battling
// enemy Unit's attack target to it.
// Identical mechanism/shape to EB01-077 Master League Begins (green.js) just
// for (Delaz Fleet) instead of (G Generation) — see its comment for the full
// pendingDefenderOverride/CHANGE_ATTACK_TARGET rationale. No printed pairing
// ability, so only the command-side [Action] trigger needs registration.
function gd06117Candidates() {
  return state.playerField.filter(c => c && c.rested && (c.trait || '').includes('(Delaz Fleet)'));
}
registerCardEffect('GD06-117', {
  canActivate: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'opp') return false;
    return gd06117Candidates().length > 0;
  },
  action: (card, ctx) => {
    const candidates = gd06117Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested friendly (Delaz Fleet) Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested friendly (Delaz Fleet) Unit. The enemy attack is redirected to it.', candidates, (target) => {
      if (target) {
        ctx.battle.defender = target;
        ctx.battle.isShield = false;
        ctx.battle.isDirect = false;
        NET.send({ type: 'CHANGE_ATTACK_TARGET', uid: target.uid });
        addLog(`${card.name} — attack redirected to ${target.name}.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD06-094 · Nyaan — Pilot ──────────────────────────────────────────────
// [When Paired] Choose 1 rested friendly Unit. It gains the following effect
// during this turn: "When this Unit deals battle damage to an enemy Unit
// that is Lv.4 or lower, destroy that enemy Unit."
// Same tempDestroyOnHit mechanism as GD04-115 Backup, just [When Paired]
// (targeting any friendly Unit, not necessarily this pilot's own) instead of
// [Main], and Lv.4 instead of Lv.5.
registerCardEffect('GD06-094', {
  pair: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Paired] no friendly Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, '[When Paired] Choose 1 friendly Unit. When it deals battle damage to an enemy Unit (Lv.4 or lower) this turn, destroy that enemy Unit.', candidates, (target) => {
      if (target) {
        target.tempDestroyOnHit = 4;
        addLog(`${card.name} — ${target.name}: when it deals battle damage to a Lv.4 or lower enemy this turn, that enemy is destroyed.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-051 · GFreD — Unit ───────────────────────────────────────────────
// [Activate･Main][Once per Turn]②, rest this Unit: Choose 1 (Alphacide) Unit
// card that is Lv.5 or lower from your trash. Deploy it.
// [Deploy] If another friendly (Alphacide) Unit is in play, this Unit gains
// the following effect during this turn: "At the end of the turn, choose 2
// of your Resources. Set them as active."
// "Rest this Unit" isn't parsed by getUnitActivationCost (which only knows
// RES cost / restUnits / oncePerTurn) — manually checked/applied here, same
// convention as ST05-003 CGS Mobile Worker. Resources are a plain state.res
// counter in this engine (no individually-tracked resource objects), so
// "choose 2, set active" is just state.res += 2 (capped at resBase) — see
// GD05-066 Shining Gundam's "[Attack] choose 1 rested Resource, set active".
function gd06051TrashCandidates() {
  return state.playerTrash.filter(c => c.type === 'unit' && (c.trait || '').includes('(Alphacide)') && (parseInt(c.level) || 0) <= 5);
}
registerCardEffect('GD06-051', {
  canActivate: (card) => !card.rested && findNextFieldSlot() !== -1 && gd06051TrashCandidates().length > 0,
  activate: (card, ctx) => {
    setRested(card, true);
    addLog(`${card.name} — rested as cost.`);
    const candidates = gd06051TrashCandidates();
    const slot = findNextFieldSlot();
    if (candidates.length === 0 || slot === -1) {
      addLog(`${card.name} — no (Alphacide) Unit card Lv.5 or lower in trash, or no field room.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Alphacide) Unit card that is Lv.5 or lower from your trash. Deploy it.', candidates, (target) => {
      if (target) deployUnitFromTrash(target, findNextFieldSlot());
      ctx.onDone();
    }, card, { mandatory: true });
  },
  deploy: (card) => {
    const hasOtherAlphacide = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Alphacide)'));
    if (!hasOtherAlphacide) return;
    card._gd06051EotGranted = true;
    addLog(`${card.name} — [Deploy] gains an end-of-turn effect: set 2 Resources active.`);
  },
  endOfTurn: (card, onDone) => {
    if (!card._gd06051EotGranted) { onDone(); return; }
    card._gd06051EotGranted = false;
    const before = state.res;
    state.res = Math.min(state.res + 2, state.resBase);
    if (state.res !== before) {
      state.resourceSetActiveThisTurn = true;
      addLog(`${card.name} — set 2 Resources active (RES now ${state.res}).`);
      broadcastCounters();
    }
    onDone();
  },
});

// ─── GD06-057 · Karius' Rick Dom II — Unit ─────────────────────────────────
// While you have another (Delaz Fleet) Unit in play, this Unit gains <Blocker>.
// ownerSide-aware field lookup (passiveKeywords is evaluated for either
// side's field from either client) — same convention as every other
// field-wide conditional passive hook.
registerCardEffect('GD06-057', {
  passiveKeywords: (card, ownerSide) => {
    const field = state[`${ownerSide}Field`] || [];
    return field.some(c => c && c.uid !== card.uid && (c.trait || '').includes('(Delaz Fleet)')) ? ['Blocker'] : [];
  },
});

// ─── GD06-049 · Gundam GP02A — Unit ────────────────────────────────────────
// [During Pair] If there are 10 or more (Zeon) Unit cards in your trash,
// destroy every enemy player's Base. (1v1: just the single opponent's Base.)
// Checked once, at the moment of pairing (the only discrete trigger point
// this game models for a standalone "[During Pair] If X, do Y" clause with
// no other bracket-trigger qualifier) — if the trash threshold is already
// met at pairing, the enemy Base is destroyed immediately.
// [Destroyed] Look at the top 3 cards of your deck. You may reveal 1 (Delaz
// Fleet) Unit card among them and add it to your hand. Place the remaining
// cards in your trash. Can't reuse lookAtTopAndPickToHand (green.js) as-is —
// that helper always returns the remainder to the bottom of the deck, but
// this card sends the remainder to the trash instead.
function destroyEnemyBaseOutright(sourceCard) {
  const base = state.oppBase;
  if (!base) return false;
  base.hp = 0;
  base.damaged = true;
  NET.send({ type: 'BASE_HP_MOD', uid: base.uid, delta: -999 });
  const removedFromGame = isRemovedFromGameBase(base);
  state.oppBase = null;
  if (!removedFromGame) {
    state[trashCountKey('opp')] += 1;
    state[trashArrayKey('opp')].push(toTrashCard(base));
  }
  addLog(`${sourceCard.name} — destroyed ${base.name}!${removedFromGame ? ' (removed from game)' : ''}`);
  triggerCardEffect(base, 'destroyed', {});
  return true;
}
registerCardEffect('GD06-049', {
  pair: (card) => {
    const zeonUnitTrashCount = state.playerTrash.filter(c => c.type === 'unit' && (c.trait || '').includes('(Zeon)')).length;
    if (zeonUnitTrashCount < 10) return;
    if (!state.oppBase) {
      addLog(`${card.name} — [During Pair] 10+ (Zeon) Unit cards in trash, but no enemy Base to destroy.`);
      return;
    }
    destroyEnemyBaseOutright(card);
    render(); setupDropZones();
  },
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    const peeked = peekTopOfDeck(3);
    if (peeked.length === 0) {
      addLog(`${card.name} — [Destroyed] deck is empty. No effect.`);
      return;
    }
    const disabledIndices = peeked
      .map((p, i) => ((p.card.type === 'unit' || p.card.type === 'unit-token') && (p.card.trait || '').includes('(Delaz Fleet)') ? -1 : i))
      .filter(i => i !== -1);
    showCardPickerModal(
      card.name,
      '[Destroyed] Look at the top 3 cards of your deck. You may reveal 1 (Delaz Fleet) Unit card among them and add it to your hand. Place the remaining cards in your trash.',
      peeked.map(p => p.card),
      (chosen) => {
        let chosenPeek = null;
        if (chosen) {
          chosenPeek = peeked.find(p => p.card.uid === chosen.uid);
          if (chosenPeek) {
            chosenPeek.entry.state = 'hand';
            state.hand.push(chosenPeek.card);
            state.deckCount = state.tempDeck.filter(isInDeck).length;
            addLog(`${card.name} — revealed ${chosen.name} and added it to hand.`);
            NET.send({ type: 'CARD_REVEAL', card: chosen, pilotName: card.name });
            broadcastCardToast(`${chosen.name} was added to hand due to ${card.name} effect.`);
          }
        }
        const remaining = chosenPeek ? peeked.filter(p => p !== chosenPeek) : peeked;
        remaining.forEach(p => {
          p.entry.state = 'trash';
          state.trashCount++;
          state.playerTrash.push(toTrashCard(p.card));
          NET.send({ type: 'DECK_MILL', card: p.card });
        });
        state.deckCount = state.tempDeck.filter(isInDeck).length;
        if (remaining.length) addLog(`${card.name} — placed ${remaining.map(p => p.card.name).join(', ')} in trash.`);
        broadcastCounters();
        render();
        setupDropZones();
      },
      card,
      { mandatory: false, disabledIndices }
    );
  },
});

// ─── GD06-093 · Anavel Gato — Pilot ────────────────────────────────────────
// [Attack] During this battle, when this Unit receives battle damage from an
// enemy Unit whose Lv. is equal to or lower than it, reduce it by 2.
// tempBattleDamageReduction — same "battle damage only, not effect damage"
// flag family as GD05-094 Quess Paraya (game.js's runDamageStep). Set on the
// paired Unit (ctx.attacker), conditional on the already-known defender at
// [Attack] time — naturally scoped to "this battle" since it's consumed
// during the same damage step and cleared at the next turn transition.
registerCardEffect('GD06-093', {
  attack: (card, ctx) => {
    const unit = ctx?.attacker;
    const defender = ctx?.defender;
    if (!unit || !defender || defender.isShield || defender.isDirect) { ctx?.onDone?.(); return; }
    const isRealUnit = state.oppField.some(c => c && c.uid === defender.uid);
    if (!isRealUnit) { ctx?.onDone?.(); return; }
    const myLevel = parseInt(unit.level) || 0;
    const enemyLevel = parseInt(defender.level) || 0;
    if (enemyLevel <= myLevel) {
      unit.tempBattleDamageReduction = (unit.tempBattleDamageReduction || 0) + 2;
      addLog(`${card.name} — this battle, incoming battle damage from ${defender.name} (Lv.${enemyLevel}) is reduced by 2.`);
    }
    ctx?.onDone?.();
  },
});

// ─── GD06-115 · Teaching New Techniques — Command ──────────────────────────
// [Main] Choose 1 enemy Unit that is Lv.4 or lower. Destroy it. Then, deploy
// 1 [Sazabi]((Neo Zeon)･AP4･HP4) Unit token. The destroyed Unit's owner
// deploys 1 [Nu Gundam]((Londo Bell)･AP3･HP3･<First Strike>) Unit token.
// Neither token variant has a matching real-card DB entry with these exact
// stats, so T-033/T-034 were added as new synthetic token DB entries — see
// assets/nuCardDatabase.json. The enemy's own token deploy uses the
// FORCE_DEPLOY_TOKEN network message (network.js) in a real networked match
// — only their client can place it on their own authoritative field — but a
// bot match has no second client to receive that message, so it silently
// no-op'd there (CONFIRMED BUG: bot never got its Nu Gundam token). Mirrors
// bounceEnemyUnit's isBotMatch branch (white.js): BotEngine already exposes
// deployTokenToOppField() for exactly this, it just wasn't wired up here.
registerCardEffect('GD06-115', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 4),
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.4 or lower. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.4 or lower. Destroy it.', candidates, (target) => {
      if (target) {
        effectDestroyEnemyUnit(target);
        deployTokenToField('T-033');
        addLog(`${card.name} — destroyed ${target.name}; deployed a [Sazabi] Unit token.`);
        if (state.isBotMatch) {
          BotEngine.deployTokenToOppField('T-034');
        } else if (state.networked) {
          NET.send({ type: 'FORCE_DEPLOY_TOKEN', cardNo: 'T-034' });
        }
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// ST11-14 batch — newly added cards, "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── ST12-015 · Two Unicorns — Command ──────────────────────────────────────
// [Action] When playing this card, choose 1 of the following effects and
// activate it:
// ■ Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.
// ■ Choose 1 friendly Unit and 1 enemy Unit that is Lv.5 or higher. Deal 2
//   damage to them.
// showChooseOneEffectModal (blue.js, GD05-102 Wings of Light) — shared
// "pick 1 of N abstract effects" menu modal. No printed Burst, so only the
// [Action] trigger needs registration (same shape as GD06-121 Crybaby Poe).
function st12015Effect(card, onDone) {
  showChooseOneEffectModal(card, [
    {
      label: 'Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.',
      run: (done) => {
        const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
        if (candidates.length === 0) {
          addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`);
          done();
          return;
        }
        showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.', candidates, (target) => {
          if (target) {
            effectDestroyEnemyUnit(target);
            addLog(`${card.name} — destroyed ${target.name}.`);
          }
          done();
        }, card, { mandatory: true });
      },
    },
    {
      label: 'Choose 1 friendly Unit and 1 enemy Unit that is Lv.5 or higher. Deal 2 damage to them.',
      run: (done) => {
        const friendly = state.playerField.filter(c => c);
        const enemies = state.oppField.filter(c => c && (parseInt(c.level) || 0) >= 5);
        if (friendly.length === 0 || enemies.length === 0) {
          addLog(`${card.name} — no valid targets. No effect.`);
          done();
          return;
        }
        showCardPickerModal(card.name, 'Choose 1 friendly Unit. Deal 2 damage to it.', friendly, (myTarget) => {
          if (!myTarget) { done(); return; }
          dealDamageToUnit(myTarget, 2);
          showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.5 or higher. Deal 2 damage to it.', enemies, (enemyTarget) => {
            if (enemyTarget) {
              dealDamageToUnit(enemyTarget, 2, { fromEnemy: true });
              addLog(`${card.name} — dealt 2 damage to ${myTarget.name} and ${enemyTarget.name}.`);
            }
            broadcastCounters(); render(); setupDropZones();
            done();
          }, card, { mandatory: true });
        }, card, { mandatory: true });
      },
    },
  ], onDone);
}
registerCardEffect('ST12-015', {
  action: (card, ctx) => st12015Effect(card, ctx.onDone),
});

// ─── T-014 · Ad Balloon token ───────────────────────────────────────────────
// This Unit can't be set as active or paired with a Pilot.
registerCardEffect('T-014', {
  cantBeSetActive: () => true,
  cantBePaired: () => true,
});

// ─── T-022 · Wire-Guided Arm token ──────────────────────────────────────────
// This Unit can't be paired with a Pilot.
registerCardEffect('T-022', {
  cantBePaired: () => true,
});

// ─── GD06-116 · Afterflow of Zeon — Command ─────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Main] Choose 1 purple (Zeon) Unit card that is Lv.5 or lower from your
// trash. Deploy it.
// Same canActivate/main + deployUnitFromTrash pattern as ST09-001 Impulse
// Gundam above.
function gd06116TrashCandidates() {
  return state.playerTrash.filter(c =>
    c.type === 'unit' &&
    (c.color || '').toLowerCase() === 'purple' &&
    (c.trait || '').includes('(Zeon)') &&
    (parseInt(c.level) || 0) <= 5
  );
}
registerCardEffect('GD06-116', {
  canActivate: () => findNextFieldSlot() !== -1 && gd06116TrashCandidates().length > 0,
  main: (card, ctx) => {
    const slot = findNextFieldSlot();
    const candidates = gd06116TrashCandidates();
    if (slot === -1 || candidates.length === 0) {
      addLog(`${card.name} — no purple (Zeon) Unit card Lv.5 or lower in trash, or no field room. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 purple (Zeon) Unit card that is Lv.5 or lower from your trash. Deploy it.', candidates, (target) => {
      if (target) deployUnitFromTrash(target, findNextFieldSlot());
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD06-055 · Gundam GP02A — Unit ─────────────────────────────────────────
// [During Pair][Attack] You may deal 1 damage to this Unit. If you do, this
// Unit gains <Breach 2> during this battle.
// Same "you may deal 1 damage to this Unit" confirm-picker + tempKeywords
// Breach grant pattern as GD05-096 Chad Chadan above / EB01-028 Gundam
// Plutone. [During Pair] gated on card.pilot being set.
registerCardEffect('GD06-055', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!card.pilot) { ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'You may deal 1 damage to this Unit. If you do, this Unit gains <Breach 2> during this battle.',
      [card],
      (chosen) => {
        if (chosen) {
          dealDamageToUnit(card, 1);
          card.tempKeywords = card.tempKeywords || [];
          if (!card.tempKeywords.includes('Breach 2')) card.tempKeywords.push('Breach 2');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'Breach 2' });
          addLog(`${card.name} — [During Pair] dealt 1 damage to itself: gains <Breach 2> this battle.`);
          render();
          setupDropZones();
        }
        ctx.onDone();
      },
      card,
      { mandatory: false, skipLabel: 'Decline' }
    );
  },
});

// ─── GD06-056 · Xamel — Unit ─────────────────────────────────────────────────
// [Attack] You may pay ②. If you do, choose 1 enemy Unit that is Lv.2 or
// lower. Destroy it.
// Same "you may pay N" confirm/skip modal as GD04-074 Kapool — gated on both
// affordability AND a valid target existing before the modal even opens (no
// legal destroy target means paying would be pure waste, unlike Kapool's
// draw/discard which always has an effect).
registerCardEffect('GD06-056', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
    if (state.res < 2 || candidates.length === 0) { ctx.onDone(); return; }

    const overlay = document.createElement('div');
    overlay.className = 'mk-overlay';
    const modal = document.createElement('div');
    modal.className = 'mk-modal';

    const title = document.createElement('div');
    title.className = 'mk-title';
    title.textContent = card.name;
    modal.appendChild(title);

    const body = document.createElement('div');
    body.className = 'mk-body';
    body.style.marginBottom = '14px';
    body.textContent = 'Pay ② to choose 1 enemy Unit Lv.2 or lower and destroy it?';
    modal.appendChild(body);

    const btnRow = document.createElement('div');
    btnRow.className = 'base-activate-btns';

    const btnYes = document.createElement('button');
    btnYes.className = 'pair-btn confirm';
    btnYes.textContent = 'Pay ②';
    btnYes.addEventListener('click', () => {
      overlay.remove();
      spendResources(2);
      broadcastCounters();
      render();
      showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.', candidates, (target) => {
        if (target) effectDestroyEnemyUnit(target);
        ctx.onDone();
      }, card, { mandatory: true });
    });

    const btnNo = document.createElement('button');
    btnNo.className = 'pair-btn cancel';
    btnNo.textContent = 'Skip';
    btnNo.addEventListener('click', () => { overlay.remove(); ctx.onDone(); });

    btnRow.appendChild(btnYes);
    btnRow.appendChild(btnNo);
    modal.appendChild(btnRow);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  },
});

// ─── GD06-060 · Zaku II F2 — Unit ────────────────────────────────────────────
// While there are 4 or more (Zeon) Unit cards in your trash, this Unit gets
// AP+1. Live-evaluated every AP read (see effectiveBp/passiveUnitApBonus in
// game.js), same convention as GD01-076 Michaelis's Command-count check.
registerCardEffect('GD06-060', {
  passiveUnitApBonus: (card, ownerSide) =>
    state[`${ownerSide}Trash`].filter(c => c.type === 'unit' && (c.trait || '').includes('(Zeon)')).length >= 4 ? 1 : 0,
});

// ─── GD06-054 · GFreD — Unit ─────────────────────────────────────────────────
// <Suppression> — the database's own nativeKeyword field is blank for this
// card (unlike e.g. GD06-077's populated "[Blocker]"), so it's granted here
// via passiveKeywords instead of relying on that field.
// [Deploy] You may choose 1 of your Units and 1 enemy Unit. Deal 1 damage to
// them. Same dual-picker shape as GD02-055 Gundam Gusion Rebake, but the
// whole effect is optional — the first picker is skippable ("you may");
// once past it the second picker is mandatory, since backing out partway
// through would leave one side hit and the other not.
registerCardEffect('GD06-054', {
  passiveKeywords: () => ['Suppression'],
  deploy: (card) => {
    const friendly = state.playerField.filter(c => c);
    const enemies = state.oppField.filter(c => c);
    if (friendly.length === 0 || enemies.length === 0) {
      addLog(`${card.name} — not enough targets. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'You may choose 1 of your Units to deal 1 damage to.', friendly, (fTarget) => {
      if (!fTarget) return;
      showCardPickerModal(card.name, 'Choose 1 enemy Unit to deal 1 damage to.', enemies, (eTarget) => {
        if (!eTarget) return;
        dealDamageToUnit(fTarget, 1);
        dealDamageToUnit(eTarget, 1, { fromEnemy: true });
        addLog(`${card.name} — dealt 1 damage to ${fTarget.name} and 1 damage to ${eTarget.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }, card, { mandatory: true });
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── GD06-052 · Val Walo — Unit ──────────────────────────────────────────────
// While there are 5 or more (Zeon) Unit cards in your trash, this Unit
// gains <High-Maneuver>. (This Unit can't be blocked.)
// [Deploy] Place the top card of your deck into your trash. If you place a
// (Zeon) card with this effect, choose 1 enemy Unit with 4 or less AP. Rest it.
// passiveKeywords condition mirrors GD06-060 Zaku II F2's own trash-count
// read elsewhere in this file; the Deploy mill is the same peekTopOfDeck +
// DECK_MILL shape as GD03-092 Nyaan in red.js, with broadcastLog (not plain
// addLog) for the "placed X into trash" line since a mill is unconditional,
// public information both players should see — unlike a private draw.
registerCardEffect('GD06-052', {
  passiveKeywords: (card, ownerSide) =>
    state[`${ownerSide}Trash`].filter(c => c.type === 'unit' && (c.trait || '').includes('(Zeon)')).length >= 5
      ? ['High-Maneuver'] : [],
  deploy: (card) => {
    const top = peekTopOfDeck(1)[0];
    if (!top) {
      addLog(`${card.name} — deck is empty, nothing to place into trash.`);
      return;
    }
    const { entry, card: milled } = top;
    entry.state = 'trash';
    state.trashCount += 1;
    state.playerTrash.push(toTrashCard(milled));
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    broadcastLog(`${card.name} — placed ${milled.name} into the trash.`);
    NET.send({ type: 'DECK_MILL', card: milled });
    broadcastCardToast(`${milled.name} was sent to trash.`, milled.img);

    const finish = () => { broadcastCounters(); render(); setupDropZones(); };
    if (!(milled.trait || '').includes('(Zeon)')) { finish(); return; }

    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 4 or less AP. No effect.`);
      finish();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 4 or less AP. Rest it.',
      candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          broadcastLog(`${card.name} — ${target.name} rested.`);
        }
        finish();
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD06-114 · Alphaciders — Command ───────────────────────────────────────
// [Action] When playing this card, choose 1 of the following effects and
// activate it:
// ■ Choose 1 friendly (Alphacide) Unit. During this battle, when it receives
//   enemy battle damage, reduce it by 3.
// ■ Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.
// Uses the existing showChooseOneEffectModal helper (see GD05-102 Wings of
// Light in blue.js — shared global scope, so it's callable from here too)
// for the 2-option choice.
function gd06114Effect(card, onDone) {
  showChooseOneEffectModal(card, [
    {
      label: 'Choose 1 friendly (Alphacide) Unit. During this battle, when it receives enemy battle damage, reduce it by 3.',
      run: (done) => {
        const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Alphacide)'));
        if (candidates.length === 0) {
          addLog(`${card.name} — no friendly (Alphacide) Units in play. No effect.`);
          done();
          return;
        }
        showCardPickerModal(
          card.name,
          'Choose 1 friendly (Alphacide) Unit. During this battle, when it receives enemy battle damage, reduce it by 3.',
          candidates,
          (target) => {
            if (target) {
              applyApMod([target], 3, 'tempBattleDamageReduction');
              addLog(`${card.name} — ${target.name} will reduce battle damage received by 3 this battle.`);
              render();
            }
            done();
          },
          card,
          { mandatory: true }
        );
      },
    },
    {
      label: 'Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.',
      run: (done) => {
        const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
        if (candidates.length === 0) {
          addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`);
          done();
          return;
        }
        showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.2 or lower. Destroy it.', candidates, (target) => {
          if (target) effectDestroyEnemyUnit(target);
          done();
        }, card, { mandatory: true });
      },
    },
  ], onDone);
}
registerCardEffect('GD06-114', {
  action: (card, ctx) => gd06114Effect(card, ctx.onDone),
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 follow-up — newly added cards
// ════════════════════════════════════════════════════════════════════════════

// ─── GD06-128 · Gwaden — Base ────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic — resolveMyShieldHit already
// handles the literal "Add this card to your hand" burst pattern.)
// [Activate･Main] Rest this Base: Choose 1 friendly (Zeon) Unit. During this
// turn, when it receives enemy battle damage, reduce it by 1.
// "Rest this Base" cost parsed generically by getBaseActivationCost(); same
// shape as ST05-015 Isaribi, target effect uses tempBattleDamageReduction
// (turn-scoped, battle-damage-only — see registry.js) like GD05-129/ST11-012.
registerCardEffect('GD06-128', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(Zeon)')),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Zeon)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Zeon) Units. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 friendly (Zeon) Unit. During this turn, when it receives enemy battle damage, reduce it by 1.', candidates, (target) => {
      if (target) {
        applyApMod([target], 1, 'tempBattleDamageReduction');
        addLog(`${card.name} — ${target.name} will reduce enemy battle damage received by 1 this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── GD06-095 · Kelley Layzner — Pilot ───────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Attack] You may deal 1 damage to this Unit. If you do, choose 1 of your
// (Zeon) Units. It gets AP+2 during this turn.
// Same "you may deal 1 damage to this Unit" confirm-picker as GD05-096 Chad
// Chadan — no "other" qualifier printed here, so the attacking Unit itself
// (it is itself (Zeon)) is a legal target for its own buff.
registerCardEffect('GD06-095', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    showCardPickerModal(
      card.name,
      'You may deal 1 damage to this Unit. If you do, choose 1 of your (Zeon) Units. It gets AP+2 during this turn.',
      [unit],
      (chosen) => {
        if (!chosen) { ctx.onDone(); return; }
        dealDamageToUnit(unit, 1);
        addLog(`${card.name} — dealt 1 damage to ${unit.name}.`);
        const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Zeon)'));
        if (candidates.length === 0) {
          addLog(`${card.name} — no friendly (Zeon) Units. No further effect.`);
          render(); setupDropZones();
          ctx.onDone();
          return;
        }
        showCardPickerModal(card.name, 'Choose 1 of your (Zeon) Units. It gets AP+2 during this turn.', candidates, (target) => {
          if (target) {
            applyApMod([target], 2, 'tempApMod');
            addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
          }
          render(); setupDropZones();
          ctx.onDone();
        }, card, { mandatory: true });
      },
      card,
      { mandatory: false, skipLabel: 'Decline' }
    );
  },
});

// ─── GD06-096 · Xavier Olivette — Pilot ──────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Attack] Place the top card of your deck into your trash. If
// you place a (Zeon)/(Alphacide) card with this effect, all enemy Units get
// AP-1 during this battle.
// isLinkUnit(ctx.attacker) — same convention as GD04-099 (white.js) for a
// Pilot's own "[During Link][Attack]" ability. Mill mechanics (DECK_MILL
// mirror, toTrashCard bookkeeping) copied from GD03-092 Nyaan/ST12-016's
// unconditional mill. battleApMod (not tempApMod) since the debuff is
// explicitly "during this battle".
registerCardEffect('GD06-096', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx.onDone?.(); return; } // [During Link]
    const top = peekTopOfDeck(1)[0];
    if (!top) {
      addLog(`${card.name} — deck is empty, nothing to place into trash.`);
      ctx.onDone?.();
      return;
    }
    const { entry, card: milled } = top;
    entry.state = 'trash';
    state.trashCount += 1;
    state.playerTrash.push(toTrashCard(milled));
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    addLog(`${card.name} — placed ${milled.name} into the trash.`);
    NET.send({ type: 'DECK_MILL', card: milled });
    broadcastCardToast(`${milled.name} was sent to trash.`);
    broadcastCounters();

    const isQualifying = (milled.trait || '').includes('(Zeon)') || (milled.trait || '').includes('(Alphacide)');
    if (isQualifying) {
      const enemies = state.oppField.filter(c => c);
      if (enemies.length > 0) {
        applyApMod(enemies, -1, 'battleApMod');
        addLog(`${card.name} — placed a (Zeon)/(Alphacide) card: all enemy Units get AP-1 during this battle.`);
      }
    }
    render(); setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD06-053 · Xavier's Gyan Hakuji-Packs (GQ) — Unit ───────────────────────
// <Blocker> native (nativeKeyword fixed in the DB — was blank).
// [Destroyed] Choose 1 of your Resources. Set it as active.
// "Choose 1 of your Resources" simplified to a plain RES recovery (resources
// are fungible up to resBase) — same convention as ST01-011/GD05-066.
registerCardEffect('GD06-053', {
  destroyed: (card, ctx) => {
    if (!ctx || ctx.ownerSide !== 'player') return;
    if (state.res >= state.resBase) {
      addLog(`${card.name} — [Destroyed] all RES already active. No effect.`);
      return;
    }
    state.res = Math.min(state.res + 1, state.resBase);
    state.resourceSetActiveThisTurn = true;
    broadcastCounters();
    addLog(`${card.name} — [Destroyed] set 1 Resource as active (RES now ${state.res}).`);
    render();
  },
});

// ─── GD06-050 · Legend Gundam — Unit ────────────────────────────────────────
// While you have a (ZAFT) Link Unit in play, this card in your trash gets
// Lv.-3 and cost -3 (only the cost half is modeled via trashDeployCostMod —
// no generic reader for a trash-level discount exists yet, and nothing in
// this batch deploys Units from trash by Lv. threshold alone).
// [Deploy] Choose 1 enemy Unit that is Lv.3 or lower. Destroy it. If you
// deploy this Unit from your trash, choose 1 to 2 enemy Units that are Lv.3
// or lower instead. ctx.fromTrash is set by deployUnitFromTrash().
registerCardEffect('GD06-050', {
  trashDeployCostMod: () =>
    state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(ZAFT)')) ? -3 : 0,
  deploy: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.3 or lower. No effect.`);
      return;
    }
    if (ctx?.fromTrash) {
      showCardPickerModalMulti(
        card.name,
        'Choose 1 to 2 enemy Units that are Lv.3 or lower. Destroy them.',
        candidates,
        { min: 1, max: 2, mandatory: true },
        (chosen) => {
          if (!chosen || chosen.length === 0) return;
          chosen.forEach(target => effectDestroyEnemyUnit(target));
          addLog(`${card.name} — destroyed ${chosen.map(c => c.name).join(', ')}.`);
          broadcastCounters(); render(); setupDropZones();
        },
        card
      );
    } else {
      showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.3 or lower. Destroy it.', candidates, (target) => {
        if (target) {
          effectDestroyEnemyUnit(target);
          addLog(`${card.name} — destroyed ${target.name}.`);
          broadcastCounters(); render(); setupDropZones();
        }
      }, card, { mandatory: true });
    }
  },
});

// ─── GD06-127 · Yomagn'tho — Base ───────────────────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Deploy] Then, if it is your turn, place the top 2 cards of your deck into
// your trash. If you place an (Alphacide) card with this effect, choose 1
// enemy Unit with 2 or less AP. Destroy it.
registerCardEffect('GD06-127', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const top = peekTopOfDeck(2);
    if (top.length === 0) {
      addLog(`${card.name} — deck is empty, nothing to mill.`);
      return;
    }
    const milled = [];
    top.forEach(({ entry, card: m }) => {
      entry.state = 'trash';
      state.trashCount++;
      state.playerTrash.push(toTrashCard(m));
      milled.push(m);
      NET.send({ type: 'DECK_MILL', card: m });
      broadcastCardToast(`${m.name} was sent to trash.`);
      addLog(`${card.name} — [Deploy] milled ${m.name} to trash.`);
    });
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    broadcastCounters(); render(); setupDropZones();
    const hasAlphacide = milled.some(m => (m.trait || '').includes('(Alphacide)'));
    if (!hasAlphacide) return;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 2 or less AP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 2 or less AP. Destroy it.', candidates, (target) => {
      if (target) {
        effectDestroyEnemyUnit(target);
        addLog(`${card.name} — destroyed ${target.name}.`);
        broadcastCounters(); render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});
